import './liquid-glass.css';
// Import chart helper functions
import {
    unadjustTimestampFromDisplay,
    adjustTimestampForDisplay,
    formatDateTime,
    formatDuration,
    getFrequencyColor,
    renderExtremeMarkers,
    updateRangeMarkers,
    renderAllChartMarkers,
    toggleExtremeMarkers,
    updatePageTitle,
    updateAnalysisButtonText,
    getXCoordinate
} from './chart-helpers.js';
import {
    state,
    mainChartOptions,
    giniChartOptions,
    seriesOptions,
    DEFAULT_TIME_RANGE,
    SYMBOLS_TO_IGNORE,
    TIME_RANGES,
    SCORE_THRESHOLD
} from './chart-config.js';

// Chart Initialization
state.chart = LightweightCharts.createChart(document.getElementById('chart'), mainChartOptions);

// Initialize HHI Chart
state.giniChart = LightweightCharts.createChart(document.getElementById('giniChart'), giniChartOptions);

// Overlay Element for Drag Selection
state.overlay = document.createElement('div');
state.overlay.className = 'absolute bg-blue-500/20 pointer-events-none hidden';
document.getElementById('chart').appendChild(state.overlay);

let currentChartTime = null;
let cursorPosition = {
    isAtLeft: null,
    logical: null
};

// Helper functions are imported from chart-helpers.js using ES6 imports

function handleCrosshairMove(param) {
    if (!param.point) {
        currentChartTime = null;
        cursorPosition = {
            isAtLeft: null,
            logical: null
        };
        return;
    }

    // Update cursor position state
    cursorPosition = {
        isAtLeft: param.logical >= 0,
        logical: param.logical
    };

    // param.time is the "fake" UTC time in seconds. Convert it back to the real UTC time.
    if (param.time) {
        currentChartTime = unadjustTimestampFromDisplay(param.time * 1000) / 1000;
    } else {
        currentChartTime = null;
    }
}

state.chart.subscribeCrosshairMove(handleCrosshairMove);
state.giniChart.subscribeCrosshairMove(handleCrosshairMove);

// Series Initialization
const btcDominanceSeries = state.chart.addSeries(LightweightCharts.LineSeries, seriesOptions.btcDominance);
const altcoinDominanceSeries = state.chart.addSeries(LightweightCharts.LineSeries, seriesOptions.altcoinDominance);
const dominanceDifferenceSeries = state.chart.addSeries(LightweightCharts.LineSeries, seriesOptions.dominanceDifference);
const btcToTop10AltDominanceLogRatioSeries = state.chart.addSeries(LightweightCharts.LineSeries, seriesOptions.btcToTop10AltDominanceLogRatio);
const hhiSeries = state.giniChart.addSeries(LightweightCharts.LineSeries, seriesOptions.hhi);

// Initialize markers primitives
let btcDominanceMarkers = LightweightCharts.createSeriesMarkers(btcDominanceSeries, []);
let altcoinDominanceMarkers = LightweightCharts.createSeriesMarkers(altcoinDominanceSeries, []);
state.dominanceDifferenceMarkers = LightweightCharts.createSeriesMarkers(dominanceDifferenceSeries, []);

// Variables Initialization
let isLocked = false;
let isDragging = false;
let dragStartX = 0;
let dragStartTime = 0;
let activeTimeRange = DEFAULT_TIME_RANGE; // Use the constant
let selectedStartTime = null;
let selectedEndTime = null;



// Add these variables at the top with other variable declarations
let currentTableFilter = 'positive';
let tabulatorTable = null;

// Add this variable near the top with other state variables
let priceChangeInUSD = false; // Default to BTC-based price changes

// Add these variables near the top with other state variables
let dominanceDifferenceStats = null;
let currentDominanceDifference = null;
let minDominanceDifference = null; // Global variable for min
let maxDominanceDifference = null; // Global variable for max

let giniStats = null;
let currentGiniIndex = null;
let currentBtcToTop10Alt = null;
let currentTop10ToSmallAlt = null;
let allDominanceStats = []; // Store stats for all timeframes
let selectedDominanceTimeframe = 'yearly'; // Default timeframe


// Variables for selected time ranges for positive and negative pct change analysis
let activeAnalysisButton = null; // 'positive' or 'negative' - Start with null

let rawDominanceData = []; // Store raw, unmodified data from the API

// Add these variables near the top with other state variables
let chartDataTimeRange = {
    min: null,
    max: null
};

/**
 * Generates a textual trading strategy report based on the statistical mean-reversion 
 * properties of cryptocurrency dominance metrics (BTC vs TOP10 vs TOP100).
 *
 * STRATEGY LOGIC:
 * The function infers two inner thresholds (p37.5 and p62.5) to create 3 action zones:
 * 
 * 1. 🚀 ENTRY (Extreme Deviation):
 *    - Value > p75: Asset A is Expensive, Asset B is Cheap -> Rebalance.
 *    - Value < p25: Asset A is Cheap, Asset B is Expensive -> Rebalance.
 * 
 * 2. 💰 TAKE PROFIT (Equilibrium):
 *    - Value [p37.5 - p62.5]: Metrics are near the Median. 
 *    - Advice: Close positions or reduce risk as the edge is gone.
 * 
 * 3. ✋ HOLD/WAIT (Transition):
 *    - Value [p25 - p37.5] OR [p62.5 - p75]: Trend is exhausting but not yet at mean.
 *    - Advice: Do not enter new positions; hold existing ones.
 *
 * @param {Object} stats - Historical statistical thresholds (yearly). 
 *        Must include keys: {metric}_{min|p25|median|p75|max}
 *        (e.g., stats.dom_diff_p75, stats.btc_top10_median).
 * 
 * @param {Object} current - Real-time calculated metrics.
 *        Must include:
 *        - dominanceDifference (BTC vs TOP100)
 *        - btcToTop10AltDominanceLogRatio (BTC vs TOP10)
 *        - top10ToSmallAltLogRatio (TOP10 vs TOP100)
 *
 * @returns {string} A formatted multi-line string containing the Action Plan, 
 *                   Risk Management advice, and Conviction Scorecard.
 */
function getStrategyAdvice(stats, current) {
    // ==========================================
    // 1. SETUP & HELPER
    // ==========================================
    const scores = { "BTC": 0, "TOP10": 0, "TOP100": 0 };
    const signals = [];     // For Entry signals (> p75 or < p25)
    const harvest = [];     // For Take Profit zone (p37.5 - p62.5)
    const hold = [];        // For Hold zone (p25-p37.5 & p62.5-p75)

    // Helper to process a specific metric
    // AssetA is the numerator, AssetB is the denominator in the ratio/diff
    const analyzeMetric = (val, statKey, label, assetA, assetB) => {
        const p25 = stats[`${statKey}_p25`];
        const median = stats[`${statKey}_median`];
        const p75 = stats[`${statKey}_p75`];
        
        // Use provided percentiles or fallback to calculation if missing
        const p37_5 = stats[`${statKey}_p37_5`] !== undefined ? stats[`${statKey}_p37_5`] : (p25 + median) / 2;
        const p62_5 = stats[`${statKey}_p62_5`] !== undefined ? stats[`${statKey}_p62_5`] : (median + p75) / 2;

        if (val > p75) {
            // EXTREME HIGH: AssetA is Expensive, AssetB is Cheap
            scores[assetA] -= 1;
            scores[assetB] += 1;
            signals.push(`${assetA} is expensive vs ${assetB} (>p75)`);
        }
        else if (val < p25) {
            // EXTREME LOW: AssetA is Cheap, AssetB is Expensive
            scores[assetA] += 1;
            scores[assetB] -= 1;
            signals.push(`${assetA} is cheap vs ${assetB} (<p25)`);
        }
        else if (val >= p37_5 && val <= p62_5) {
            // EQUILIBRIUM ZONE: Reversion to mean completed
            harvest.push(`${label} is at Equilibrium (take profit)`);
        }
        else {
            // NO MANS LAND: Between p25-p37.5 OR p62.5-p75
            hold.push(`${label} is in Hold Zone (trend exhausting)`);
        }
    };

    // ==========================================
    // 2. RUN CALCULATIONS
    // ==========================================

    // Metric 1: BTC vs TOP100 (Small) -> Key: dom_diff
    analyzeMetric(current.dominanceDifference, 'dom_diff', 'BTC/TOP100', 'BTC', 'TOP100');

    // Metric 2: BTC vs TOP10 (Large) -> Key: btc_top10
    analyzeMetric(current.btcToTop10AltDominanceLogRatio, 'btc_top10', 'BTC/TOP10', 'BTC', 'TOP10');

    // Metric 3: TOP10 vs TOP100 -> Key: top10_small
    analyzeMetric(current.top10ToSmallAltLogRatio, 'top10_small', 'TOP10/TOP100', 'TOP10', 'TOP100');


    // ==========================================
    // 3. TEXT GENERATION
    // ==========================================

    // Categorize Directional Bias
    const longs = Object.keys(scores).filter(k => scores[k] > 0);
    const shorts = Object.keys(scores).filter(k => scores[k] < 0);

    let output = "";

    // --- SECTION A: IMMEDIATE ACTION (ENTRIES) ---
    if (longs.length > 0 || shorts.length > 0) {
        output += "🚀 ACTION PLAN: ENTER POSITIONS\n";
        output += "-------------------------------\n";
        if (longs.length > 0) output += `🟢 LONG : ${longs.join(' + ')}\n`;
        if (shorts.length > 0) output += `🔴 SHORT: ${shorts.join(' + ')}\n`;
        output += "\n";
    }

    // --- SECTION B: MANAGEMENT (EXITS / HOLDS) ---
    if (harvest.length > 0 || hold.length > 0) {
        output += "🛡️ RISK MANAGEMENT\n";
        output += "------------------\n";

        if (harvest.length > 0) {
            output += "💰 TAKE PROFIT / REDUCE RISK:\n";
            harvest.forEach(h => output += `   • ${h}\n`);
        }

        if (hold.length > 0) {
            if (harvest.length > 0) output += "\n"; // spacer
            output += "✋ HOLD / WAIT (No New Entries):\n";
            hold.forEach(h => output += `   • ${h}\n`);
        }
        output += "\n";
    }

    // --- SECTION C: NEUTRAL FALLBACK ---
    // If absolutely nothing happened (rare, but possible if code fails)
    if (output === "") {
        output += "⚖️ MARKET NEUTRAL\n";
        output += "-----------------\n";
        output += "No statistical anomalies detected.\n\n";
    }

    // --- SECTION D: DETAILED EXPLANATION ---
    output += "📊 STATISTICAL CONTEXT\n";
    output += "----------------------\n";

    // 1. Signals Breakdown
    if (signals.length > 0) {
        output += "Active Signals (Outer Bands):\n";
        signals.forEach(s => output += `  • ${s}\n`);
    } else {
        output += "No extreme deviations (>p75 or <p25) detected.\n";
    }

    // 2. Scorecard Visual
    output += "\nConviction Scorecard:\n";
    Object.keys(scores).forEach(asset => {
        const score = scores[asset];
        // Visual bar: Green for positive, Red for negative
        const bar = score > 0 ? "🟩".repeat(score) : score < 0 ? "🟥".repeat(Math.abs(score)) : "⬜";
        const sign = score > 0 ? "+" : "";
        output += `  ${asset.padEnd(7)}: ${sign}${score} ${bar}\n`;
    });

    return output;
}

function updateStrategyAdvice() {
    const adviceContainer = document.getElementById('strategyAdviceContainer');
    const adviceContent = document.getElementById('strategyAdviceContent');

    try {
        // Get the latest data point from the raw dominance data
        if (rawDominanceData.length === 0) {
            adviceContent.textContent = "Waiting for data...";
            adviceContainer.classList.remove('hidden');
            return;
        }
        const current = rawDominanceData[rawDominanceData.length - 1];

        // Find stats for the selected timeframe
        const stats = allDominanceStats.find(s => s.timeframe === selectedDominanceTimeframe);
        if (!stats) {
            console.warn(`Stats for timeframe ${selectedDominanceTimeframe} not found`);
            return;
        }

        // Generate the advice
        const advice = getStrategyAdvice(stats, current);

        // Display the advice
        adviceContent.textContent = advice;
        adviceContainer.classList.remove('hidden');

    } catch (error) {
        console.error("Error updating strategy advice:", error);
        adviceContent.textContent = "Could not load strategy advice.";
        adviceContainer.classList.remove('hidden');
    }
}

let top10Coins = new Set(); // Store top 10 coins

// Function to fetch top 10 coins
// Function to fetch top 10 coins
// Function to fetch price summary
async function fetchPriceSummary() {
    try {
        const response = await fetch(`${window.location.origin}/api/crypto/price-changes/summary`);
        if (response.ok) {
            return await response.json();
        } else {
            console.error("Failed to fetch price summary");
            return [];
        }
    } catch (error) {
        console.error("Error fetching price summary:", error);
        return [];
    }
}

// Function to fetch top 10 coins
async function fetchTop10Coins() {
    try {
        const [coinsResponse, priceSummary] = await Promise.all([
            fetch(`${window.location.origin}/api/crypto/coins/top10`),
            fetchPriceSummary()
        ]);

        if (coinsResponse.ok) {
            const coins = await coinsResponse.json();
            top10Coins = new Set(coins);
            console.log("Fetched top 10 coins:", top10Coins);

            // Create a map for price changes
            const priceChangeMap = new Map();
            priceSummary.forEach(item => {
                priceChangeMap.set(item.symbol, item.change24h);
            });

            // Dynamic Color Generation
            const getColorForSymbol = (symbol) => {
                const colors = [
                    'text-orange-500', 'text-orange-400',
                    'text-purple-400', 'text-purple-500',
                    'text-blue-400', 'text-blue-500', 'text-blue-300',
                    'text-yellow-400', 'text-yellow-500',
                    'text-green-400', 'text-green-500', 'text-green-300',
                    'text-red-500', 'text-red-400',
                    'text-pink-500', 'text-pink-400',
                    'text-indigo-400', 'text-indigo-500',
                    'text-teal-400', 'text-cyan-400'
                ];
                
                let hash = 0;
                for (let i = 0; i < symbol.length; i++) {
                    hash = symbol.charCodeAt(i) + ((hash << 5) - hash);
                }
                
                const index = Math.abs(hash) % colors.length;
                return colors[index];
            };

            // Render coins
            const container = document.getElementById('top10CoinsList');
            if (container) {
                container.innerHTML = ''; // Clear loading state
                const coinsArray = Array.from(coins);
                
                coinsArray.forEach((coin, index) => {
                    // Coin Container
                    const coinContainer = document.createElement('div');
                    coinContainer.className = 'flex items-center gap-2';

                    // Coin Symbol
                    const coinSpan = document.createElement('span');
                    const colorClass = getColorForSymbol(coin);
                    coinSpan.className = `font-bold text-lg tracking-wide ${colorClass} hover:opacity-80 transition-opacity cursor-default`;
                    coinSpan.textContent = coin;
                    coinContainer.appendChild(coinSpan);

                    // Price Change
                    const change24h = priceChangeMap.get(coin);
                    if (change24h !== undefined && change24h !== null) {
                        const changeSpan = document.createElement('span');
                        const isPositive = change24h >= 0;
                        changeSpan.className = `text-xs font-medium ${isPositive ? 'text-green-400' : 'text-red-400'}`;
                        changeSpan.textContent = `${isPositive ? '+' : ''}${change24h.toFixed(2)}%`;
                        coinContainer.appendChild(changeSpan);
                    }

                    container.appendChild(coinContainer);

                    // Separator (don't add after the last item)
                    if (index < coinsArray.length - 1) {
                        const separator = document.createElement('div');
                        separator.className = 'h-4 w-px bg-gray-700 mx-2'; // Vertical line style
                        container.appendChild(separator);
                    }
                });
            }
        } else {
            console.error("Failed to fetch top 10 coins");
            const container = document.getElementById('top10CoinsList');
            if (container) {
                container.innerHTML = '<span class="text-red-400 text-sm">Failed to load top 10 coins</span>';
            }
        }
    } catch (error) {
        console.error("Error fetching top 10 coins:", error);
        const container = document.getElementById('top10CoinsList');
        if (container) {
            container.innerHTML = '<span class="text-red-400 text-sm">Error loading top 10 coins</span>';
        }
    }
}

// Call fetchTop10Coins on load
fetchTop10Coins();

// Function to fetch funding information
async function fetchFundingData() {
    try {
        const response = await fetch(`${window.location.origin}/api/crypto/funding`);
        if (response.ok) {
            const data = await response.json();
            renderTopFundingRevenue(data);
        } else {
            console.error("Failed to fetch funding data");
            const container = document.getElementById('top5FundingList');
            if (container) {
                container.innerHTML = '<span class="text-red-400 text-sm">Failed to load funding data</span>';
            }
        }
    } catch (error) {
        console.error("Error fetching funding data:", error);
        const container = document.getElementById('top5FundingList');
        if (container) {
            container.innerHTML = '<span class="text-red-400 text-sm">Error loading funding data</span>';
        }
    }
}

// Function to render top 5 funding revenue symbols
function renderTopFundingRevenue(data) {
    const container = document.getElementById('top5FundingList');
    if (!container) return;

    if (!data || data.length === 0) {
        container.innerHTML = '<span class="text-gray-400 text-sm">No funding data available</span>';
        return;
    }

    // Sort by revenue descending and take top 5
    const top5 = [...data]
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 5);

    container.innerHTML = ''; // Clear loading state

    top5.forEach((item, index) => {
        const itemContainer = document.createElement('div');
        itemContainer.className = 'flex items-center gap-3';

        // Symbol
        const symbolSpan = document.createElement('span');
        symbolSpan.className = 'font-bold text-lg tracking-wide text-indigo-400';
        symbolSpan.textContent = item.symbol;
        itemContainer.appendChild(symbolSpan);

        // Interval
        const intervalSpan = document.createElement('span');
        intervalSpan.className = 'text-xs px-2 py-0.5 rounded bg-indigo-500/20 text-indigo-300 font-mono';
        intervalSpan.textContent = `${item.interval}h`;
        itemContainer.appendChild(intervalSpan);

        // Daily Rate
        const rateSpan = document.createElement('span');
        const ratePercent = (item.dailyRate * 100).toFixed(4);
        const isPositive = item.dailyRate >= 0;
        rateSpan.className = `text-sm font-semibold ${isPositive ? 'text-emerald-400' : 'text-rose-400'}`;
        rateSpan.textContent = `${isPositive ? '+' : ''}${ratePercent}%`;
        itemContainer.appendChild(rateSpan);

        container.appendChild(itemContainer);

        // Separator
        if (index < top5.length - 1) {
            const separator = document.createElement('div');
            separator.className = 'h-4 w-px bg-gray-700 mx-2';
            container.appendChild(separator);
        }
    });
}

// Call fetchFundingData on load
fetchFundingData();

// Dominance Timeframe Selector Event Listeners
document.getElementById('dominanceTimeframeSelector').addEventListener('click', (e) => {
    const button = e.target.closest('button');
    if (!button) return;

    const timeframe = button.dataset.timeframe;
    if (!timeframe) return;

    // Update state
    selectedDominanceTimeframe = timeframe;

    // Update UI buttons
    const buttons = document.querySelectorAll('#dominanceTimeframeSelector button');
    buttons.forEach(btn => {
        btn.dataset.active = btn.dataset.timeframe === timeframe ? 'true' : 'false';
    });

    console.log(`Switched dominance timeframe to: ${timeframe}`);

    // Update gauges and strategy advice
    updateLinearGauges();
    updateStrategyAdvice();
});

// Use updatePageTitle instead

// Event listeners for Positive/Negative Pct Change buttons
document.getElementById('positivePctChangeButton').addEventListener('click', () => {
    const posButton = document.getElementById('positivePctChangeButton');
    const negButton = document.getElementById('negativePctChangeButton');
    if (posButton.dataset.active === 'true') {
        // Deactivate if already active
        posButton.dataset.active = 'false';
        activeAnalysisButton = null;
        console.log("Positive range selection deactivated.");
    } else {
        // Activate positive, deactivate negative
        posButton.dataset.active = 'true';
        negButton.dataset.active = 'false';
        activeAnalysisButton = 'positive';
        console.log("Next drag/duration click will set the POSITIVE range.");
    }
    // Update lock state based on button activity
    isLocked = posButton.dataset.active === 'true' || negButton.dataset.active === 'true';
    console.log("Chart locked:", isLocked);
});

document.getElementById('negativePctChangeButton').addEventListener('click', () => {
    const posButton = document.getElementById('positivePctChangeButton');
    const negButton = document.getElementById('negativePctChangeButton');
    if (negButton.dataset.active === 'true') {
        // Deactivate if already active
        negButton.dataset.active = 'false';
        activeAnalysisButton = null;
        console.log("Negative range selection deactivated.");
    } else {
        // Activate negative, deactivate positive
        negButton.dataset.active = 'true';
        posButton.dataset.active = 'false';
        activeAnalysisButton = 'negative';
        console.log("Next drag/duration click will set the NEGATIVE range.");
    }
    // Update lock state based on button activity
    isLocked = posButton.dataset.active === 'true' || negButton.dataset.active === 'true';
    console.log("Chart locked:", isLocked);
});

// Double-click listeners to clear ranges
document.getElementById('positivePctChangeButton').addEventListener('dblclick', () => {
    console.log("Double-click detected on Positive button. Clearing range.");
    state.positivePctChangeTimeRange = { startTime: null, endTime: null, duration: null };
    const button = document.getElementById('positivePctChangeButton');
    updateAnalysisButtonText('positivePctChangeButton', null, null);
    button.dataset.hasData = 'false';
    // Deactivate if it was active for selection
    if (activeAnalysisButton === 'positive') {
        activeAnalysisButton = null;
        button.dataset.active = 'false';
        isLocked = document.getElementById('negativePctChangeButton').dataset.active === 'true'; // Check if other button is active
        console.log("Positive range selection deactivated by double-click.");
        console.log("Chart locked:", isLocked);
    }
    updateRangeMarkers();
    renderAllChartMarkers();
    // Optionally clear or refetch price changes table here if needed
    // Refetch price changes if the other range is still set, otherwise hide the table
    const negRangeSet = state.negativePctChangeTimeRange.startTime || state.negativePctChangeTimeRange.duration;
    if (negRangeSet) {
        fetchPriceChanges(
            null, null, null, // Cleared positive range
            state.negativePctChangeTimeRange.startTime, state.negativePctChangeTimeRange.endTime, state.negativePctChangeTimeRange.duration
        );
    } else {
        // Both ranges are now clear, hide the container
        document.getElementById('priceChangesContainer').classList.add('hidden');
        document.getElementById('priceChangesContainer').innerHTML = ''; // Clear content
    }
});

document.getElementById('negativePctChangeButton').addEventListener('dblclick', () => {
    console.log("Double-click detected on Negative button. Clearing range.");
    state.negativePctChangeTimeRange = { startTime: null, endTime: null, duration: null };
    const button = document.getElementById('negativePctChangeButton');
    updateAnalysisButtonText('negativePctChangeButton', null, null);
    button.dataset.hasData = 'false';
    // Deactivate if it was active for selection
    if (activeAnalysisButton === 'negative') {
        activeAnalysisButton = null;
        button.dataset.active = 'false';
        isLocked = document.getElementById('positivePctChangeButton').dataset.active === 'true'; // Check if other button is active
        console.log("Negative range selection deactivated by double-click.");
        console.log("Chart locked:", isLocked);
    }
    updateRangeMarkers();
    renderAllChartMarkers();
    // Optionally clear or refetch price changes table here if needed
    // Refetch price changes if the other range is still set, otherwise hide the table
    const posRangeSet = state.positivePctChangeTimeRange.startTime || state.positivePctChangeTimeRange.duration;
    if (posRangeSet) {
        fetchPriceChanges(
            state.positivePctChangeTimeRange.startTime, state.positivePctChangeTimeRange.endTime, state.positivePctChangeTimeRange.duration,
            null, null, null // Cleared negative range
        );
    } else {
        // Both ranges are now clear, hide the container
        document.getElementById('priceChangesContainer').classList.add('hidden');
        document.getElementById('priceChangesContainer').innerHTML = ''; // Clear content
    }
});


isLocked = false; // Initially unlocked

// Use getXCoordinate instead

// Chart Interaction Handlers - Drag Start
const chartElement = document.getElementById('chart');

const startDrag = (clientX) => {
    // Allow dragging anytime to select a range, remove isLocked dependency
    // Only allow drag start if at least one analysis button is active (i.e., chart is "locked" for selection)
    if (!isLocked) return false;

    dragStartTime = currentChartTime;

    const rect = chartElement.getBoundingClientRect();
    dragStartX = clientX - rect.left;
    state.overlay.style.left = dragStartX + 'px';
    state.overlay.style.width = '0';
    state.overlay.style.height = '20px';
    state.overlay.classList.remove('hidden');
    isDragging = true;
    document.getElementById('dragInfo').classList.remove('hidden');

    // Temporarily disable chart interactions during drag
    state.chart.applyOptions({
        handleScroll: false,
        handleScale: false
    });
    return true;
}

// Chart Interaction Handlers - Dragging
const handleDrag = (clientX) => {
    // Dragging logic should only depend on isDragging state
    if (!isDragging) return;

    const rect = chartElement.getBoundingClientRect();
    const currentX = clientX - rect.left;
    const width = currentX - dragStartX;

    let endTime = currentChartTime * 1000;
    let startTime = dragStartTime * 1000;

    if (startTime > endTime) {
        let t = endTime;
        endTime = startTime;
        startTime = t;
    }

    state.overlay.style.width = Math.abs(width) + 'px';
    state.overlay.style.left = (width < 0 ? currentX : dragStartX) + 'px';


    document.getElementById('startTime').textContent = formatDateTime(startTime);
    document.getElementById('endTime').textContent = formatDateTime(endTime);
    document.getElementById('durationDisplay').textContent = formatDuration(
        startTime,
        endTime
    );
};

// Chart Interaction Handlers - Drag End
const endDrag = async (clientX) => {
    // Allow ending drag anytime it was started
    if (!isDragging) return;

    isDragging = false;
    state.overlay.classList.add('hidden');
    document.getElementById('dragInfo').classList.add('hidden');

    let endTime = currentChartTime * 1000;
    let startTime = dragStartTime * 1000;

    if (startTime > endTime) {
        let t = endTime;
        endTime = startTime;
        startTime = t;
    }

    // Update displays
    document.getElementById('selectedStartTime').textContent = formatDateTime(startTime);
    document.getElementById('selectedEndTime').textContent = formatDateTime(endTime);
    document.getElementById('selectedDuration').textContent = formatDuration(startTime, endTime);
    document.getElementById('selectedTimeInfo').classList.remove('hidden');

    // If no analysis button is active, don't store range or fetch
    if (!activeAnalysisButton) {
        console.log("Drag ended, but no analysis button active. No range stored.");
        // Re-enable chart interactions even if no analysis button was active
        state.chart.applyOptions({ handleScroll: true, handleScale: true });

        return; // Exit early
    }

    // Store the selected range based on the active button
    if (activeAnalysisButton === 'positive') {
        state.positivePctChangeTimeRange = { startTime, endTime, duration: null };
        console.log("Positive range set by drag:", state.positivePctChangeTimeRange);
        updateAnalysisButtonText('positivePctChangeButton', startTime, endTime);
        document.getElementById('positivePctChangeButton').dataset.hasData = 'true';
    } else {
        state.negativePctChangeTimeRange = { startTime, endTime, duration: null };
        console.log("Negative range set by drag:", state.negativePctChangeTimeRange);
        updateAnalysisButtonText('negativePctChangeButton', startTime, endTime);
        document.getElementById('negativePctChangeButton').dataset.hasData = 'true';
    }
    // Update and render markers after drag
    updateRangeMarkers();
    renderAllChartMarkers();

    // Deactivate the active button after successful drag
    const activeButton = document.getElementById(activeAnalysisButton === 'positive' ? 'positivePctChangeButton' : 'negativePctChangeButton');
    activeButton.dataset.active = 'false';
    console.log(`Deactivated ${activeAnalysisButton} button after drag selection.`);
    activeAnalysisButton = null;
    isLocked = false;

    // Check if the positive range is set (either by start/end or duration)
    const posRangeSet = state.positivePctChangeTimeRange.startTime || state.positivePctChangeTimeRange.duration;
    // Check if the negative range is set (needed for the fetch call parameters)
    const negRangeSet = state.negativePctChangeTimeRange.startTime || state.negativePctChangeTimeRange.duration;

    // Fetch if the drag was significant and the positive range is set
    if (endTime - startTime > 180000 && posRangeSet) {
        // Determine negative range parameters to pass
        const negStartTimeParam = negRangeSet ? state.negativePctChangeTimeRange.startTime : null;
        const negEndTimeParam = negRangeSet ? state.negativePctChangeTimeRange.endTime : null;
        const negDurationParam = negRangeSet ? state.negativePctChangeTimeRange.duration : null;

        // Pass positive range and conditional negative range parameters.
        await fetchPriceChanges(
            state.positivePctChangeTimeRange.startTime, state.positivePctChangeTimeRange.endTime, state.positivePctChangeTimeRange.duration,
            negStartTimeParam,
            negEndTimeParam,
            negDurationParam
        );
    }

    // Re-enable chart interactions after drag
    state.chart.applyOptions({
        handleScroll: true,
        handleScale: true
    });
};

// Chart Interaction Event Listeners - Mouse Events
chartElement.addEventListener('mousedown', e => {
    if (startDrag(e.clientX)) {
        const mousemoveHandler = (e) => handleDrag(e.clientX);
        const mouseupHandler = (e) => {
            endDrag(e.clientX);
            document.removeEventListener('mousemove', mousemoveHandler);
            document.removeEventListener('mouseup', mouseupHandler);
        };
        document.addEventListener('mousemove', mousemoveHandler);
        document.addEventListener('mouseup', mouseupHandler);
    }
});

// Chart Interaction Event Listeners - Touch Events
chartElement.addEventListener('touchstart', e => {
    if (startDrag(getXCoordinate(e))) {
        const touchmoveHandler = (e) => handleDrag(getXCoordinate(e));
        const touchendHandler = (e) => {
            endDrag(getXCoordinate(e));
            document.removeEventListener('touchmove', touchmoveHandler);
            document.removeEventListener('touchend', touchendHandler);
            document.removeEventListener('touchcancel', touchendHandler); // Optional: handle touch cancel
        };
        document.addEventListener('touchmove', touchmoveHandler);
        document.addEventListener('touchend', touchendHandler);
        document.addEventListener('touchcancel', touchendHandler); // Optional: handle touch cancel
    }
}, { passive: false }); // passive: false is needed to prevent scrolling during touchmove if needed

// Function to Fetch Price Changes Data and Render Table
// Modify function signature to accept separate time ranges/durations
async function fetchPriceChanges(
    posStartTime, posEndTime, posDuration,
    negStartTime, negEndTime, negDuration
) {
    const limit = 1000;
    const priceChangesContainer = document.getElementById('priceChangesContainer');
    const loadingTemplate = document.getElementById('loadingTemplate');

    // Determine if we need to fetch positive/negative data
    const fetchPositive = !!(posStartTime || posDuration);
    const fetchNegative = !!(negStartTime || negDuration);

    // If neither range is specified, hide the container and exit
    if (!fetchPositive && !fetchNegative) {
        priceChangesContainer.classList.add('hidden');
        priceChangesContainer.innerHTML = ''; // Clear content
        console.log("No time range specified for either positive or negative analysis. Hiding table.");
        return;
    }

    try {
        // Show container and loading state using template
        priceChangesContainer.classList.remove('hidden');
        priceChangesContainer.innerHTML = ''; // Clear existing content
        if (loadingTemplate) {
            const loadingContent = loadingTemplate.content.cloneNode(true);
            priceChangesContainer.appendChild(loadingContent);
        } else {
            console.error("Loading template not found!");
            priceChangesContainer.textContent = 'Loading...'; // Fallback text
        }

        // Build the API URLs conditionally
        let apiUrlPositive = null;
        if (fetchPositive) {
            apiUrlPositive = `${window.location.origin}/api/crypto/price-changes?limit=${limit}&inUSD=${priceChangeInUSD}`;
            if (posDuration) {
                apiUrlPositive += `&duration=${posDuration}`;
            } else if (posStartTime) {
                apiUrlPositive += `&startTime=${moment(posStartTime).toISOString()}`;
                if (posEndTime) apiUrlPositive += `&endTime=${moment(posEndTime).toISOString()}`;
            }
        } else {
            console.warn("No time range specified for positive price change analysis. Skipping fetch.");
        }

        let apiUrlNegative = null;
        if (fetchNegative) {
            apiUrlNegative = `${window.location.origin}/api/crypto/price-changes?limit=${limit}&inUSD=${priceChangeInUSD}`;
            if (negDuration) {
                apiUrlNegative += `&duration=${negDuration}`;
            } else if (negStartTime) {
                apiUrlNegative += `&startTime=${moment(negStartTime).toISOString()}`;
                if (negEndTime) apiUrlNegative += `&endTime=${moment(negEndTime).toISOString()}`;
            }
        } else {
            console.warn("No time range specified for negative price change analysis. Skipping fetch.");
        }

        // Fetch data conditionally in parallel: positive changes, negative changes, and summary
        const positiveFetchPromise = fetchPositive
            ? fetch(apiUrlPositive).then(res => res.json())
            : Promise.resolve([]); // Resolve with empty array if not fetching

        const negativeFetchPromise = fetchNegative
            ? fetch(apiUrlNegative).then(res => res.json())
            : Promise.resolve([]); // Resolve with empty array if not fetching

        const summaryFetchPromise = fetchPriceSummary();

        const [positiveData, negativeData, summaryData] = await Promise.all([
            positiveFetchPromise,
            negativeFetchPromise,
            summaryFetchPromise
        ]);

        // Reset container content using template
        const headerTemplate = document.getElementById('priceAnalysisHeaderTemplate');
        priceChangesContainer.innerHTML = ''; // Clear existing content
        if (headerTemplate) {
            const headerContent = headerTemplate.content.cloneNode(true);
            priceChangesContainer.appendChild(headerContent);
            // Create and append the div where the table will be mounted
            const tableDiv = document.createElement('div');
            tableDiv.id = 'price-changes-table';
            priceChangesContainer.appendChild(tableDiv);
        } else {
            console.error("Price analysis header template not found!");
            // Fallback: Add a simple header and the table div
            priceChangesContainer.innerHTML = '<h2 class="text-xl font-semibold mb-4">Price Analysis</h2><div id="price-changes-table"></div>';
        }

        // Create maps for faster lookup
        const summaryMap = new Map();
        summaryData.forEach(item => {
            summaryMap.set(item.coinId, item);
        });

        // Merge data first.
        const mergedMap = new Map();

        // Process positive data (if fetched)
        if (fetchPositive) {
            positiveData.forEach(item => {
                const summary = summaryMap.get(item.coinId) || {};
                mergedMap.set(item.coinId, {
                    ...item, // Includes symbol, change1h, change24h etc. from positive call
                    ...summary,
                    positivePtcChange: item.pctChange, // Rename pctChange
                    negativePtcChange: null // Initialize negative field
                });
            });
        }

        // Process negative data (if fetched) and merge
        if (fetchNegative) {
            negativeData.forEach(item => {
                const existing = mergedMap.get(item.coinId);
                if (existing) {
                    existing.negativePtcChange = item.pctChange; // Add the negative pctChange
                } else {
                    // Handle case where coin exists in negative but not positive
                    const summary = summaryMap.get(item.coinId) || {};
                    mergedMap.set(item.coinId, {
                        ...item,
                        ...summary,
                        positivePtcChange: null, // Initialize positive field
                        negativePtcChange: item.pctChange
                    });
                }
            });
        }

        // If neither was fetched, but summary was, populate map with summary data
        if (!fetchPositive && !fetchNegative) {
            summaryData.forEach(item => {
                if (!mergedMap.has(item.coinId)) { // Avoid overwriting if somehow populated
                    mergedMap.set(item.coinId, {
                        ...item, // Summary data
                        positivePtcChange: null,
                        negativePtcChange: null
                    });
                }
            });
        }

        // Filter out the symbols that should be ignored
        const filteredData = Array.from(mergedMap.values()).filter(item => !SYMBOLS_TO_IGNORE.includes(item.symbol));

        // Compute thresholds for each scoring column based on filtered data.
        // const columnsToScore = [
        //     'positivePtcChange', 'negativePtcChange', 'change24h', 'change7d', 'change30d',
        //     'change1dAgo', 'change2dAgo', 'change3dAgo',
        //     'change4dAgo', 'change5dAgo', 'change6dAgo'
        // ];

        const columnsToScore = [
            'positivePtcChange', 'negativePtcChange', 'change7d', 'change30d'
        ];

        const thresholds = {};
        columnsToScore.forEach(col => {
            // Only compute thresholds if the corresponding data was fetched
            if (col === 'positivePtcChange' && !fetchPositive) return;
            if (col === 'negativePtcChange' && !fetchNegative) return;

            const values = filteredData.map(data => data[col]).filter(v => typeof v === 'number');
            values.sort((a, b) => a - b);
            if (values.length) {
                thresholds[col] = {
                    bottom: values[Math.floor(values.length * SCORE_THRESHOLD.bottom_percent)],
                    top: values[Math.floor(values.length * SCORE_THRESHOLD.top_percent)]
                };
            }
        });
        // Save thresholds globally for cell formatters to use
        state.columnThresholds = thresholds;

        // Compute scores comparing each coin's column values to its thresholds.
        const finalData = filteredData.map(data => {
            let score = 0;
            const scoreLog = [`[Score Calculation] Symbol: ${data.symbol}`];

            // Score positivePtcChange (only if fetched)
            if (fetchPositive) {
                const posValue = data.positivePtcChange;
                const posThresholds = thresholds['positivePtcChange'];
                if (typeof posValue === 'number' && posThresholds) {
                    if (posValue >= posThresholds.top) {
                        score += 6;
                        scoreLog.push(`  +2 (positivePtcChange >= top: ${posValue.toFixed(2)} >= ${posThresholds.top.toFixed(2)})`);
                    } else if (posValue <= posThresholds.bottom) {
                        score -= 6;
                        scoreLog.push(`  -2 (positivePtcChange <= bottom: ${posValue.toFixed(2)} <= ${posThresholds.bottom.toFixed(2)})`);
                    }
                } else if (posValue === null) {
                    scoreLog.push(`  +0 (positivePtcChange: N/A)`);
                }
            } else {
                scoreLog.push(`  +0 (positivePtcChange: Not Fetched)`);
            }

            // Score negativePtcChange (only if fetched)
            if (fetchNegative) {
                const negValue = data.negativePtcChange;
                const negThresholds = thresholds['negativePtcChange'];
                if (typeof negValue === 'number' && negThresholds) {
                    if (negValue >= negThresholds.top) {
                        score -= 6; // Subtract points for high negative change
                        scoreLog.push(`  -2 (negativePtcChange >= top: ${negValue.toFixed(2)} >= ${negThresholds.top.toFixed(2)})`);
                    } else if (negValue <= negThresholds.bottom) {
                        score += 6; // Add points for low negative change
                        scoreLog.push(`  +2 (negativePtcChange <= bottom: ${negValue.toFixed(2)} <= ${negThresholds.bottom.toFixed(2)})`);
                    }
                } else if (negValue === null) {
                    scoreLog.push(`  +0 (negativePtcChange: N/A)`);
                }
            } else {
                scoreLog.push(`  +0 (negativePtcChange: Not Fetched)`);
            }

            // Score other columns (1 point each)
            columnsToScore.forEach(col => {
                // Skip pctChange as it's handled separately
                if (col === 'positivePtcChange' || col === 'negativePtcChange') return;

                const value = data[col];
                const colThresholds = thresholds[col];
                if (typeof value === 'number' && colThresholds) {
                    const points = 1; // All other columns get 1 point
                    if (value >= colThresholds.top) {
                        score += points;
                        scoreLog.push(`  +${points} (${col} >= top: ${value.toFixed(2)} >= ${colThresholds.top.toFixed(2)})`);
                    } else if (value <= colThresholds.bottom) {
                        score -= points;
                        scoreLog.push(`  -${points} (${col} <= bottom: ${value.toFixed(2)} <= ${colThresholds.bottom.toFixed(2)})`);
                    }
                } else if (value === null) {
                    scoreLog.push(`  +0 (${col}: N/A)`);
                }
            });

            scoreLog.push(`  Final Score: ${score}`);
            console.log(scoreLog.join('\n')); // Log the detailed score calculation

            return { ...data, score };
        });

        // Use finalData for Tabulator table initialization.
        tabulatorTable = new Tabulator("#price-changes-table", {
            data: finalData,
            layout: "fitColumns",
            responsiveLayout: "hide",
            placeholder: "No data available",
            rowFormatter: function (row) {
                const data = row.getData();
                if (top10Coins.has(data.symbol)) {
                    row.getElement().classList.add("top-10-highlight");
                }
            },
            columns: [
                {
                    title: "Symbol",
                    field: "symbol",
                    sorter: "string",
                    cssClass: "symbol-cell",
                    headerSort: true,
                    minWidth: 100,
                    resizable: false
                },
                {
                    title: "Pos Pct Change", // Renamed title
                    field: "positivePtcChange", // Updated field
                    sorter: "number",
                    headerSort: true,
                    minWidth: 30,
                    formatter: function (cell) {
                        const value = cell.getValue();
                        const formatted = value?.toFixed(2) || 'N/A';
                        cell.getElement().classList.remove('positive-value', 'negative-value', 'neutral-value');
                        if (value != null) {
                            const field = cell.getColumn().getField();
                            const colThreshold = state.columnThresholds && state.columnThresholds[field];
                            if (colThreshold) {
                                if (value >= colThreshold.top) {
                                    cell.getElement().classList.add('positive-value');
                                } else if (value <= colThreshold.bottom) {
                                    cell.getElement().classList.add('negative-value');
                                } else {
                                    cell.getElement().classList.add('neutral-value');
                                }
                            } else {
                                // Fallback behavior if thresholds not computed (e.g., data not fetched)
                                if (value > 0) {
                                    cell.getElement().classList.add('positive-value');
                                } else if (value < 0) {
                                    cell.getElement().classList.add('negative-value');
                                } else {
                                    cell.getElement().classList.add('neutral-value');
                                }
                            }
                        }
                        return value != null ? `${formatted}%` : 'N/A';
                    }
                },
                {
                    title: "Neg Pct Change", // New column title
                    field: "negativePtcChange", // New field
                    sorter: "number",
                    headerSort: true,
                    minWidth: 30,
                    formatter: function (cell) { // Same formatter logic, applied to the new field
                        const value = cell.getValue();
                        const formatted = value?.toFixed(2) || 'N/A';
                        cell.getElement().classList.remove('positive-value', 'negative-value', 'neutral-value');
                        if (value != null) {
                            const field = cell.getColumn().getField();
                            const colThreshold = state.columnThresholds && state.columnThresholds[field];
                            if (colThreshold) {
                                // Note: Coloring based on value, not score contribution
                                if (value >= colThreshold.top) {
                                    cell.getElement().classList.add('positive-value'); // High negative change is still 'positive' in raw value terms if > 0
                                } else if (value <= colThreshold.bottom) {
                                    cell.getElement().classList.add('negative-value'); // Low negative change is still 'negative' in raw value terms if < 0
                                } else {
                                    cell.getElement().classList.add('neutral-value');
                                }
                            } else {
                                // Fallback behavior if thresholds not computed (e.g., data not fetched)
                                if (value > 0) {
                                    cell.getElement().classList.add('positive-value');
                                } else if (value < 0) {
                                    cell.getElement().classList.add('negative-value');
                                } else {
                                    cell.getElement().classList.add('neutral-value');
                                }
                            }
                        }
                        return value != null ? `${formatted}%` : 'N/A';
                    }
                },
                {
                    title: "1h Change",
                    field: "change1h",
                    sorter: "number",
                    headerSort: true,
                    minWidth: 30,
                    formatter: function (cell) {
                        const value = cell.getValue();
                        const formatted = value?.toFixed(2) || 'N/A';
                        cell.getElement().classList.remove('positive-value', 'negative-value', 'neutral-value');
                        if (value != null) {
                            const field = cell.getColumn().getField();
                            const colThreshold = state.columnThresholds && state.columnThresholds[field];
                            if (colThreshold) {
                                if (value >= colThreshold.top) {
                                    cell.getElement().classList.add('positive-value');
                                } else if (value <= colThreshold.bottom) {
                                    cell.getElement().classList.add('negative-value');
                                } else {
                                    cell.getElement().classList.add('neutral-value');
                                }
                            } else {
                                // Fallback behavior if thresholds not computed
                                if (value > 0) {
                                    cell.getElement().classList.add('positive-value');
                                } else if (value < 0) {
                                    cell.getElement().classList.add('negative-value');
                                } else {
                                    cell.getElement().classList.add('neutral-value');
                                }
                            }
                        }
                        return value != null ? `${formatted}%` : 'N/A';
                    }
                },
                {
                    title: "24h Change",
                    field: "change24h",
                    sorter: "number",
                    headerSort: true,
                    minWidth: 30,
                    formatter: function (cell) {
                        const value = cell.getValue();
                        const formatted = value?.toFixed(2) || 'N/A';
                        cell.getElement().classList.remove('positive-value', 'negative-value', 'neutral-value');
                        if (value != null) {
                            const field = cell.getColumn().getField();
                            const colThreshold = state.columnThresholds && state.columnThresholds[field];
                            if (colThreshold) {
                                if (value >= colThreshold.top) {
                                    cell.getElement().classList.add('positive-value');
                                } else if (value <= colThreshold.bottom) {
                                    cell.getElement().classList.add('negative-value');
                                } else {
                                    cell.getElement().classList.add('neutral-value');
                                }
                            } else {
                                // Fallback behavior if thresholds not computed
                                if (value > 0) {
                                    cell.getElement().classList.add('positive-value');
                                } else if (value < 0) {
                                    cell.getElement().classList.add('negative-value');
                                } else {
                                    cell.getElement().classList.add('neutral-value');
                                }
                            }
                        }
                        return value != null ? `${formatted}%` : 'N/A';
                    }
                },
                {
                    title: "1 Week Change",
                    field: "change7d",
                    sorter: "number",
                    headerSort: true,
                    minWidth: 30,
                    formatter: function (cell) {
                        const value = cell.getValue();
                        const formatted = value?.toFixed(2) || 'N/A';
                        cell.getElement().classList.remove('positive-value', 'negative-value', 'neutral-value');
                        if (value != null) {
                            const field = cell.getColumn().getField();
                            const colThreshold = state.columnThresholds && state.columnThresholds[field];
                            if (colThreshold) {
                                if (value >= colThreshold.top) {
                                    cell.getElement().classList.add('positive-value');
                                } else if (value <= colThreshold.bottom) {
                                    cell.getElement().classList.add('negative-value');
                                } else {
                                    cell.getElement().classList.add('neutral-value');
                                }
                            } else {
                                // Fallback behavior if thresholds not computed
                                if (value > 0) {
                                    cell.getElement().classList.add('positive-value');
                                } else if (value < 0) {
                                    cell.getElement().classList.add('negative-value');
                                } else {
                                    cell.getElement().classList.add('neutral-value');
                                }
                            }
                        }
                        return value != null ? `${formatted}%` : 'N/A';
                    }
                },
                {
                    title: "1 Month Change",
                    field: "change30d",
                    sorter: "number",
                    headerSort: true,
                    minWidth: 30,
                    formatter: function (cell) {
                        const value = cell.getValue();
                        const formatted = value?.toFixed(2) || 'N/A';
                        cell.getElement().classList.remove('positive-value', 'negative-value', 'neutral-value');
                        if (value != null) {
                            const field = cell.getColumn().getField();
                            const colThreshold = state.columnThresholds && state.columnThresholds[field];
                            if (colThreshold) {
                                if (value >= colThreshold.top) {
                                    cell.getElement().classList.add('positive-value');
                                } else if (value <= colThreshold.bottom) {
                                    cell.getElement().classList.add('negative-value');
                                } else {
                                    cell.getElement().classList.add('neutral-value');
                                }
                            } else {
                                // Fallback behavior if thresholds not computed
                                if (value > 0) {
                                    cell.getElement().classList.add('positive-value');
                                } else if (value < 0) {
                                    cell.getElement().classList.add('negative-value');
                                } else {
                                    cell.getElement().classList.add('neutral-value');
                                }
                            }
                        }
                        return value != null ? `${formatted}%` : 'N/A';
                    }
                },
                {
                    title: "1D Ago",
                    field: "change1dAgo",
                    sorter: "number",
                    headerSort: true,
                    minWidth: 30,
                    formatter: function (cell) {
                        const value = cell.getValue();
                        const formatted = value?.toFixed(2) || 'N/A';
                        cell.getElement().classList.remove('positive-value', 'negative-value', 'neutral-value');
                        if (value != null) {
                            const field = cell.getColumn().getField();
                            const colThreshold = state.columnThresholds && state.columnThresholds[field];
                            if (colThreshold) {
                                if (value >= colThreshold.top) {
                                    cell.getElement().classList.add('positive-value');
                                } else if (value <= colThreshold.bottom) {
                                    cell.getElement().classList.add('negative-value');
                                } else {
                                    cell.getElement().classList.add('neutral-value');
                                }
                            } else {
                                // Fallback behavior if thresholds not computed
                                if (value > 0) {
                                    cell.getElement().classList.add('positive-value');
                                } else if (value < 0) {
                                    cell.getElement().classList.add('negative-value');
                                } else {
                                    cell.getElement().classList.add('neutral-value');
                                }
                            }
                        }
                        return value != null ? `${formatted}%` : 'N/A';
                    }
                },
                {
                    title: "2D Ago",
                    field: "change2dAgo",
                    sorter: "number",
                    headerSort: true,
                    minWidth: 30,
                    formatter: function (cell) {
                        const value = cell.getValue();
                        const formatted = value?.toFixed(2) || 'N/A';
                        cell.getElement().classList.remove('positive-value', 'negative-value', 'neutral-value');
                        if (value != null) {
                            const field = cell.getColumn().getField();
                            const colThreshold = state.columnThresholds && state.columnThresholds[field];
                            if (colThreshold) {
                                if (value >= colThreshold.top) {
                                    cell.getElement().classList.add('positive-value');
                                } else if (value <= colThreshold.bottom) {
                                    cell.getElement().classList.add('negative-value');
                                } else {
                                    cell.getElement().classList.add('neutral-value');
                                }
                            } else {
                                // Fallback behavior if thresholds not computed
                                if (value > 0) {
                                    cell.getElement().classList.add('positive-value');
                                } else if (value < 0) {
                                    cell.getElement().classList.add('negative-value');
                                } else {
                                    cell.getElement().classList.add('neutral-value');
                                }
                            }
                        }
                        return value != null ? `${formatted}%` : 'N/A';
                    }
                },
                {
                    title: "3D Ago",
                    field: "change3dAgo",
                    sorter: "number",
                    headerSort: true,
                    minWidth: 30,
                    responsive: 2,
                    formatter: function (cell) {
                        const value = cell.getValue();
                        const formatted = value?.toFixed(2) || 'N/A';
                        cell.getElement().classList.remove('positive-value', 'negative-value', 'neutral-value');
                        if (value != null) {
                            const field = cell.getColumn().getField();
                            const colThreshold = state.columnThresholds && state.columnThresholds[field];
                            if (colThreshold) {
                                if (value >= colThreshold.top) {
                                    cell.getElement().classList.add('positive-value');
                                } else if (value <= colThreshold.bottom) {
                                    cell.getElement().classList.add('negative-value');
                                } else {
                                    cell.getElement().classList.add('neutral-value');
                                }
                            } else {
                                // Fallback behavior if thresholds not computed
                                if (value > 0) {
                                    cell.getElement().classList.add('positive-value');
                                } else if (value < 0) {
                                    cell.getElement().classList.add('negative-value');
                                } else {
                                    cell.getElement().classList.add('neutral-value');
                                }
                            }
                        }
                        return value != null ? `${formatted}%` : 'N/A';
                    }
                },
                {
                    title: "4D Ago",
                    field: "change4dAgo",
                    sorter: "number",
                    headerSort: true,
                    minWidth: 30,
                    responsive: 2,
                    formatter: function (cell) {
                        const value = cell.getValue();
                        const formatted = value?.toFixed(2) || 'N/A';
                        cell.getElement().classList.remove('positive-value', 'negative-value', 'neutral-value');
                        if (value != null) {
                            const field = cell.getColumn().getField();
                            const colThreshold = state.columnThresholds && state.columnThresholds[field];
                            if (colThreshold) {
                                if (value >= colThreshold.top) {
                                    cell.getElement().classList.add('positive-value');
                                } else if (value <= colThreshold.bottom) {
                                    cell.getElement().classList.add('negative-value');
                                } else {
                                    cell.getElement().classList.add('neutral-value');
                                }
                            } else {
                                // Fallback behavior if thresholds not computed
                                if (value > 0) {
                                    cell.getElement().classList.add('positive-value');
                                } else if (value < 0) {
                                    cell.getElement().classList.add('negative-value');
                                } else {
                                    cell.getElement().classList.add('neutral-value');
                                }
                            }
                        }
                        return value != null ? `${formatted}%` : 'N/A';
                    }
                },
                {
                    title: "5D Ago",
                    field: "change5dAgo",
                    sorter: "number",
                    headerSort: true,
                    minWidth: 30,
                    responsive: 2,
                    formatter: function (cell) {
                        const value = cell.getValue();
                        const formatted = value?.toFixed(2) || 'N/A';
                        cell.getElement().classList.remove('positive-value', 'negative-value', 'neutral-value');
                        if (value != null) {
                            const field = cell.getColumn().getField();
                            const colThreshold = state.columnThresholds && state.columnThresholds[field];
                            if (colThreshold) {
                                if (value >= colThreshold.top) {
                                    cell.getElement().classList.add('positive-value');
                                } else if (value <= colThreshold.bottom) {
                                    cell.getElement().classList.add('negative-value');
                                } else {
                                    cell.getElement().classList.add('neutral-value');
                                }
                            } else {
                                // Fallback behavior if thresholds not computed
                                if (value > 0) {
                                    cell.getElement().classList.add('positive-value');
                                } else if (value < 0) {
                                    cell.getElement().classList.add('negative-value');
                                } else {
                                    cell.getElement().classList.add('neutral-value');
                                }
                            }
                        }
                        return value != null ? `${formatted}%` : 'N/A';
                    }
                },
                {
                    title: "6D Ago",
                    field: "change6dAgo",
                    sorter: "number",
                    headerSort: true,
                    minWidth: 30,
                    responsive: 2,
                    formatter: function (cell) {
                        const value = cell.getValue();
                        const formatted = value?.toFixed(2) || 'N/A';
                        cell.getElement().classList.remove('positive-value', 'negative-value', 'neutral-value');
                        if (value != null) {
                            const field = cell.getColumn().getField();
                            const colThreshold = state.columnThresholds && state.columnThresholds[field];
                            if (colThreshold) {
                                if (value >= colThreshold.top) {
                                    cell.getElement().classList.add('positive-value');
                                } else if (value <= colThreshold.bottom) {
                                    cell.getElement().classList.add('negative-value');
                                } else {
                                    cell.getElement().classList.add('neutral-value');
                                }
                            } else {
                                // Fallback behavior if thresholds not computed
                                if (value > 0) {
                                    cell.getElement().classList.add('positive-value');
                                } else if (value < 0) {
                                    cell.getElement().classList.add('negative-value');
                                } else {
                                    cell.getElement().classList.add('neutral-value');
                                }
                            }
                        }
                        return value != null ? `${formatted}%` : 'N/A';
                    }
                },
                // New Score column added to dump the computed score
                {
                    title: "Score",
                    field: "score",
                    sorter: "number",
                    headerSort: true,
                    minWidth: 30,
                    formatter: "plaintext"
                }
            ],
            initialSort: [
                { column: "score", dir: 'desc' }
            ],
            // Remove the old rowFormatter that calculated greenPoints/redPoints
            dataFiltered: function (filters, rows) {
                console.log(`Showing ${rows.length} rows after filtering`);
            }
        });

        // Add filter function
        function applyTableFilter(filterType) {
            if (!tabulatorTable) return;
            currentTableFilter = filterType;

            // Update button states
            document.querySelectorAll('[data-filter]').forEach(btn => {
                btn.dataset.active = btn.dataset.filter === filterType ? 'true' : 'false';
            });

            if (filterType === 'all') {
                tabulatorTable.clearFilter();
            } else {
                // Compute thresholds based on current data
                const data = tabulatorTable.getData();
                if (!data.length) return;
                const scores = data.map(d => d.score).sort((a, b) => a - b);
                const bottomThreshold = scores[Math.floor(scores.length * SCORE_THRESHOLD.global_bottom_percent)];
                const topThreshold = scores[Math.floor(scores.length * SCORE_THRESHOLD.global_top_percent)];

                tabulatorTable.setFilter(function (rowData) {
                    if (filterType === 'positive') {
                        return rowData.score >= topThreshold;
                    } else if (filterType === 'negative') {
                        return rowData.score <= bottomThreshold;
                    }
                    return true;
                });
            }
        }

        // Add event listeners for filter buttons
        document.querySelectorAll('[data-filter]').forEach(button => {
            button.addEventListener('click', () => {
                applyTableFilter(button.dataset.filter);
            });
        });

        // Unlock the chart after data is loaded
        isLocked = false;
        // const lockButton = document.getElementById('positivePctChangeButton'); // Use new ID if needed for UI update
        // const icon = lockButton.querySelector('i');
        // icon.classList.remove('fa-lock'); // Remove lock icon logic
        // icon.classList.add('fa-lock-open'); // Remove lock icon logic
        // lockButton.querySelector('span').textContent = 'Positive Pct Change'; // Update text if needed
        state.chart.applyOptions({
            handleScroll: true,
            handleScale: true
        });

        // After creating the table, add an event listener to compute thresholds and update highlight classes:
        tabulatorTable.on("tableBuilt", function () {
            const rows = tabulatorTable.getRows();
            if (rows.length === 0) return;
            // Collect all scores, then sort in ascending order
            const scores = rows.map(row => row.getData().score).sort((a, b) => a - b);
            const bottomThreshold = scores[Math.floor(scores.length * SCORE_THRESHOLD.global_bottom_percent)];
            const topThreshold = scores[Math.floor(scores.length * SCORE_THRESHOLD.global_top_percent)];

            rows.forEach(row => {
                const data = row.getData();
                const el = row.getElement();
                el.classList.remove('highlight-row', 'negative-highlight-row');
                if (data.score >= topThreshold) {
                    el.classList.add('highlight-row');
                } else if (data.score <= bottomThreshold) {
                    el.classList.add('negative-highlight-row');
                }
                // Retain hover style
                el.classList.add('hover:bg-gray-700/50');
            });
            // Apply initial filter - now setting to 'positive' by default
            applyTableFilter('positive');
        });

    } catch (error) {
        console.error('Error fetching price changes:', error);
        // Show error state using template
        const errorTemplate = document.getElementById('errorTemplate');
        priceChangesContainer.innerHTML = ''; // Clear existing content
        if (errorTemplate) {
            const errorContent = errorTemplate.content.cloneNode(true);
            priceChangesContainer.appendChild(errorContent);
        } else {
            console.error("Error template not found!");
            // Fallback text
            priceChangesContainer.innerHTML = '<h2 class="text-xl font-semibold mb-4">Price Analysis</h2><p class="text-red-500 p-12">Error loading price changes. Please try again.</p>';
        }
    }
}

// Function to toggle view mode
function toggleViewMode(mode) {
    state.currentViewMode = mode;

    if (mode === 'split') {
        // Show BTC and Altcoin series
        btcDominanceSeries.applyOptions({ visible: true });
        altcoinDominanceSeries.applyOptions({ visible: true });
        dominanceDifferenceSeries.applyOptions({ visible: false });
        btcToTop10AltDominanceLogRatioSeries.applyOptions({ visible: false });

        // Clear markers when dominanceDifferenceSeries is hidden
        btcDominanceMarkers.setMarkers([]);
        altcoinDominanceMarkers.setMarkers([]);
        state.dominanceDifferenceMarkers.setMarkers([]);

        // Update buttons
        document.getElementById('viewModeButton').dataset.active = 'true';
        document.getElementById('aggrModeButton').dataset.active = 'false';
    } else {
        // Show Difference series
        btcDominanceSeries.applyOptions({ visible: false });
        altcoinDominanceSeries.applyOptions({ visible: false });
        dominanceDifferenceSeries.applyOptions({ visible: true });
        btcToTop10AltDominanceLogRatioSeries.applyOptions({ visible: true });

        // Re-add markers when switching back to difference view
        if (state.extremeMarkersEnabled) {
            renderExtremeMarkers();
        }

        // Update buttons
        document.getElementById('viewModeButton').dataset.active = 'false';
        document.getElementById('aggrModeButton').dataset.active = 'true';
    }

    // Re-render markers after changing view mode
    if (state.extremeMarkersEnabled && state.extremeTimestamps.length > 0) {
        renderExtremeMarkers();
    }
}

// Add event listeners for view mode buttons
document.getElementById('viewModeButton').addEventListener('click', () => toggleViewMode('split'));
document.getElementById('aggrModeButton').addEventListener('click', () => toggleViewMode('aggr'));

// Add this function to fetch the difference summary
async function fetchDominanceDifferenceStats() {
    try {
        // Use pre-fetched data if available, otherwise fetch it now
        dominanceDifferenceStats = await dominanceDifferenceStatsPromise ||
            fetch(`${window.location.origin}/api/crypto/dominance/difference/summary`)
                .then(response => response.json());

        updateDominanceMeter();
    } catch (error) {
        console.error('Error fetching dominance difference stats:', error);
    }
}

function updateDominanceMeter() {
    if (!dominanceDifferenceStats || currentDominanceDifference === null) return;

    let { histogram } = dominanceDifferenceStats;

    // Initialize global min/max if null, otherwise use existing global values
    if (minDominanceDifference === null || maxDominanceDifference === null) {
        minDominanceDifference = dominanceDifferenceStats.minDominanceDifference;
        maxDominanceDifference = dominanceDifferenceStats.maxDominanceDifference;
    }

    // Update min and max values if current value is outside the range
    if (currentDominanceDifference < minDominanceDifference) {
        minDominanceDifference = currentDominanceDifference;
    }
    if (currentDominanceDifference > maxDominanceDifference) {
        maxDominanceDifference = currentDominanceDifference;
    }

    const range = maxDominanceDifference - minDominanceDifference;
    // Avoid division by zero if min and max are the same
    const position = range === 0 ? 50 : ((currentDominanceDifference - minDominanceDifference) / range) * 100;
    // Ensure position is within 0-100 range
    const clampedPosition = Math.max(0, Math.min(100, position));

    // Calculate max frequency for normalization
    const maxFrequency = histogram && histogram.length > 0
        ? histogram.reduce((max, bucket) => Math.max(max, bucket.frequency), 0)
        : 0;

    // --- Indicator Color Logic based on Frequency Tier ---
    let indicatorColor = 'rgb(107 114 128)'; // Default to gray-500

    if (histogram && histogram.length > 0 && maxFrequency > 0) {
        const currentBucket = histogram.find(bucket =>
            currentDominanceDifference >= bucket.rangeStart && currentDominanceDifference <= bucket.rangeEnd
        );

        if (currentBucket) {
            const frequencyRatio = currentBucket.frequency / maxFrequency;
            indicatorColor = getFrequencyColor(frequencyRatio);
        }
    }
    // --- End Indicator Color Logic ---


    // Update the meter position and values
    const meter = document.getElementById('dominanceMeter');
    const indicator = document.getElementById('dominanceIndicator');
    const currentValue = document.getElementById('currentDominanceValue');
    const minValue = document.getElementById('minDominanceValue');
    const maxValue = document.getElementById('maxDominanceValue');

    indicator.style.left = `${clampedPosition}%`;
    currentValue.textContent = currentDominanceDifference.toFixed(2);
    minValue.textContent = minDominanceDifference.toFixed(2);
    maxValue.textContent = maxDominanceDifference.toFixed(2);

    // Apply the calculated color to the indicator
    indicator.style.backgroundColor = indicatorColor;

    // --- Dynamic Meter Background Color based on Distinct Frequency Tiers ---
    if (histogram && histogram.length > 0 && range > 0 && maxFrequency > 0) {
        const gradientStops = histogram.flatMap((bucket) => {
            const frequencyRatio = bucket.frequency / maxFrequency;
            // Get the color for this bucket based on its frequency
            const color = getFrequencyColor(frequencyRatio);

            // Calculate stop positions for the start and end of the bucket range
            const startPosition = ((bucket.rangeStart - minDominanceDifference) / range) * 100;
            const endPosition = ((bucket.rangeEnd - minDominanceDifference) / range) * 100;

            // Create two stops for a solid color block: one at the start, one at the end
            return [
                `${color} ${startPosition.toFixed(2)}%`,
                `${color} ${endPosition.toFixed(2)}%`
            ];
        });

        // Create the CSS linear gradient string
        const gradient = `linear-gradient(to right, ${gradientStops.join(', ')})`;

        // Apply the gradient to the meter background
        meter.style.background = gradient;
    } else {
        // Fallback if no histogram data or zero range
        meter.style.background = 'rgb(55 65 81)'; // gray-700
    }
    // --- End Dynamic Meter Background Color ---

    // Update dominanceDifferenceStats with the potentially new min and max values
    dominanceDifferenceStats.minDominanceDifference = minDominanceDifference;
    dominanceDifferenceStats.maxDominanceDifference = maxDominanceDifference;
}

// Add this function to fetch the Gini summary
async function fetchGiniStats() {
    try {
        // Use pre-fetched data if available, otherwise fetch it now
        giniStats = await giniStatsPromise ||
            fetch(`${window.location.origin}/api/crypto/dominance/gini/summary`)
                .then(response => response.json());

        updateGiniMeter();
    } catch (error) {
        console.error('Error fetching Gini stats:', error);
    }
}

function updateGiniMeter() {
    if (!giniStats || currentGiniIndex === null) return;


    let { histogram } = giniStats;
    let { minGiniIndex, maxGiniIndex } = giniStats;

    // Update min and max values if current value is outside the range
    if (currentGiniIndex < minGiniIndex) {
        minGiniIndex = currentGiniIndex;
    }
    if (currentGiniIndex > maxGiniIndex) {
        maxGiniIndex = currentGiniIndex;
    }

    const range = maxGiniIndex - minGiniIndex;
    // Avoid division by zero if min and max are the same
    const position = range === 0 ? 50 : ((currentGiniIndex - minGiniIndex) / range) * 100;
    // Ensure position is within 0-100 range
    const clampedPosition = Math.max(0, Math.min(100, position));

    // Calculate max frequency for normalization
    const maxFrequency = histogram && histogram.length > 0
        ? histogram.reduce((max, bucket) => Math.max(max, bucket.frequency), 0)
        : 0;

    // --- Indicator Color Logic based on Frequency Tier ---
    let indicatorColor = 'rgb(107 114 128)'; // Default to gray-500

    if (histogram && histogram.length > 0 && maxFrequency > 0) {
        const currentBucket = histogram.find(bucket =>
            currentGiniIndex >= bucket.rangeStart && currentGiniIndex <= bucket.rangeEnd
        );

        if (currentBucket) {
            const frequencyRatio = currentBucket.frequency / maxFrequency;
            indicatorColor = getFrequencyColor(frequencyRatio);
        }
    }
    // --- End Indicator Color Logic ---


    // Update the meter position and values
    const meter = document.getElementById('giniMeter');
    const indicator = document.getElementById('giniIndicator');
    const currentValue = document.getElementById('currentGiniValue');
    const minValue = document.getElementById('minGiniValue');
    const maxValue = document.getElementById('maxGiniValue');

    indicator.style.left = `${clampedPosition}%`;
    currentValue.textContent = currentGiniIndex.toFixed(4); // Gini index is usually 0 to 1, so 4 decimal places
    minValue.textContent = minGiniIndex.toFixed(4);
    maxValue.textContent = maxGiniIndex.toFixed(4);

    // Apply the calculated color to the indicator
    indicator.style.backgroundColor = indicatorColor;

    // --- Dynamic Meter Background Color based on Distinct Frequency Tiers ---
    if (histogram && histogram.length > 0 && range > 0 && maxFrequency > 0) {
        const gradientStops = histogram.flatMap((bucket) => {
            const frequencyRatio = bucket.frequency / maxFrequency;
            // Get the color for this bucket based on its frequency
            const color = getFrequencyColor(frequencyRatio);

            // Calculate stop positions for the start and end of the bucket range
            const startPosition = ((bucket.rangeStart - minGiniIndex) / range) * 100;
            const endPosition = ((bucket.rangeEnd - minGiniIndex) / range) * 100;

            // Create two stops for a solid color block: one at the start, one at the end
            return [
                `${color} ${startPosition.toFixed(2)}%`,
                `${color} ${endPosition.toFixed(2)}%`
            ];
        });

        // Create the CSS linear gradient string
        const gradient = `linear-gradient(to right, ${gradientStops.join(', ')})`;

        // Apply the gradient to the meter background
        meter.style.background = gradient;
    } else {
        // Fallback if no histogram data or zero range
        meter.style.background = 'rgb(55 65 81)'; // gray-700
    }
    // --- End Dynamic Meter Background Color ---

    // Update giniStats with the potentially new min and max values
    giniStats.minGiniIndex = minGiniIndex;
    giniStats.maxGiniIndex = maxGiniIndex;
}


// adjustTimestampForDisplay is imported from chart-helpers.js

// Add this helper function to process dominance data
async function processDominanceData(data) {
    // If new data is provided, update the raw data store. Otherwise, use existing raw data.
    if (data) {
        // Sort data by timestamp once and store it
        data.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        rawDominanceData = data;
    }

    // Track the min and max times in the chart data (using original timestamps)
    if (rawDominanceData.length > 0) {
        chartDataTimeRange.min = new Date(rawDominanceData[0].timestamp).getTime();
        chartDataTimeRange.max = new Date(rawDominanceData[rawDominanceData.length - 1].timestamp).getTime();
        console.log(`Chart data range: ${new Date(chartDataTimeRange.min).toISOString()} to ${new Date(chartDataTimeRange.max).toISOString()}`);
    }

    // Transform data for each series using the adjusted timestamps
    const btcData = rawDominanceData.map(item => ({
        time: adjustTimestampForDisplay(item.timestamp) / 1000,
        value: item.btcDominance,
    }));

    const altcoinData = rawDominanceData.map(item => ({
        time: adjustTimestampForDisplay(item.timestamp) / 1000,
        value: item.altcoinDominanceExclTop10,
    }));

    const differenceData = rawDominanceData.map(item => ({
        time: adjustTimestampForDisplay(item.timestamp) / 1000,
        value: item.dominanceDifference,
    }));

    const btcToTop10AltDominanceLogRatioData = rawDominanceData.map(item => ({
        time: adjustTimestampForDisplay(item.timestamp) / 1000,
        value: item.btcToTop10AltDominanceLogRatio,
    }));

    // Filter out null values for giniAltExclTop10Stable before mapping
    const hhiData = rawDominanceData
        .filter(item => item.giniAltExclTop10Stable !== null)
        .map(item => ({
            time: adjustTimestampForDisplay(item.timestamp) / 1000,
            value: item.giniAltExclTop10Stable,
        }));

    // Update series data
    btcDominanceSeries.setData(btcData);
    altcoinDominanceSeries.setData(altcoinData);
    dominanceDifferenceSeries.setData(differenceData);
    btcToTop10AltDominanceLogRatioSeries.setData(btcToTop10AltDominanceLogRatioData);
    hhiSeries.setData(hhiData); // Set data for the new HHI series

    // Set initial visibility based on current view mode
    toggleViewMode(state.currentViewMode);

    state.chart.priceScale("right").applyOptions({ "autoScale": true });
    state.chart.priceScale("left").applyOptions({ "autoScale": true });
    state.giniChart.priceScale("right").applyOptions({ "autoScale": true }); // Auto scale HHI chart

    // Update current dominance difference if data exists
    if (rawDominanceData.length > 0) {
        const latestData = rawDominanceData[rawDominanceData.length - 1];
        currentDominanceDifference = latestData.dominanceDifference;
        updateDominanceMeter();
        updatePageTitle(currentDominanceDifference);

        // Update current Gini index if data exists
        currentGiniIndex = latestData.giniAltExclTop10Stable;
        updateGiniMeter();

        // Update current values for linear gauges
        currentBtcToTop10Alt = latestData.btcToTop10AltDominanceLogRatio;
        currentTop10ToSmallAlt = latestData.top10ToSmallAltLogRatio;


        // Update linear gauges with current values
        updateLinearGauges();
        updateStrategyAdvice();
    }

    // After updating the chart data range, re-fetch extreme timestamps to get only those in range
    await fetchExtremeTimestamps();
    renderAllChartMarkers();
}

// Modify the fetchData function to use the helper
async function fetchData(forceRefresh = false) {
    try {
        // If forceRefresh is true, always fetch fresh data regardless of time range
        if (forceRefresh) {
            // For 'All' time range, fetch all data without time constraints
            if (activeTimeRange === 'All') {
                const response = await fetch(`${window.location.origin}/api/crypto/dominance/btc`);
                const data = await response.json();
                await processDominanceData(data);
                state.chart.timeScale().fitContent();
                return;
            }
            // For other time ranges including DEFAULT_TIME_RANGE, fetch with time constraints
            else {
                const rangeMs = TIME_RANGES[activeTimeRange];
                const currentEndTime = new Date().getTime();
                const currentStartTime = currentEndTime - rangeMs;

                const startTimeISO = new Date(currentStartTime).toISOString();
                const endTimeISO = new Date(currentEndTime).toISOString();

                const response = await fetch(`${window.location.origin}/api/crypto/dominance/btc?startTime=${startTimeISO}&endTime=${endTimeISO}`);
                const data = await response.json();
                await processDominanceData(data);

                // Set chart visible range
                state.chart.timeScale().setVisibleRange({
                    from: currentStartTime / 1000,
                    to: currentEndTime / 1000
                });
                return;
            }
        }

        // If not forceRefresh, follow the normal flow
        if (activeTimeRange === 'All') {
            const response = await fetch(`${window.location.origin}/api/crypto/dominance/btc`);
            const data = await response.json();
            if (data) {
                await processDominanceData(data);
                state.chart.timeScale().fitContent();
                return;
            }
        }

        // For the default '3M' time range, use the pre-fetched data
        if (activeTimeRange === DEFAULT_TIME_RANGE && dominanceDataPromise) {
            const data = await dominanceDataPromise;
            if (data) {
                await processDominanceData(data);
                // Set chart visible range for the default time range
                state.chart.timeScale().setVisibleRange({
                    from: startTime / 1000,
                    to: endTime / 1000
                });
                return;
            }
        }

        // For other time ranges or if pre-fetched data failed, call setTimeRange
        setTimeRange(TIME_RANGES[activeTimeRange]);
    } catch (error) {
        console.error('Error fetching data:', error);
    }
}

// Function to Set Time Range for the Chart
function setTimeRange(rangeMs) {
    // Calculate the time range
    const endTime = new Date().getTime();
    const startTime = endTime - rangeMs;

    // Update our chartDataTimeRange immediately
    chartDataTimeRange.min = startTime;
    chartDataTimeRange.max = endTime;

    // Format dates for API call
    const startTimeISO = new Date(startTime).toISOString();
    const endTimeISO = new Date(endTime).toISOString();

    // Fetch data with the specified time range
    fetch(`${window.location.origin}/api/crypto/dominance/btc?startTime=${startTimeISO}&endTime=${endTimeISO}`)
        .then(response => response.json())
        .then(data => {
            processDominanceData(data);

            // Set chart visible range - this is specific to setTimeRange
            state.chart.timeScale().setVisibleRange({
                from: startTime / 1000,
                to: endTime / 1000
            });
        })
        .catch(error => {
            console.error('Error fetching time range data:', error);
        });
}

// Function to Set Active Button State in Button Groups
function setActiveButton(activeButton) {
    activeButton.parentElement.querySelectorAll('button').forEach(btn => {
        btn.dataset.active = 'false';
    });
    activeButton.dataset.active = 'true';
}


// Event Listeners - Time Range Buttons
document.querySelectorAll('[data-time]').forEach(button => {
    button.addEventListener('click', (e) => {
        activeTimeRange = e.target.dataset.time;
        setActiveButton(e.target);
        if (activeTimeRange === 'All') {
            state.chart.timeScale().fitContent();
        } else {
            setTimeRange(TIME_RANGES[activeTimeRange]);
        }
    });
});

// Event Listeners - Default Duration Buttons
document.querySelectorAll('#defaultDurationButtons button').forEach(button => {
    button.addEventListener('click', function () {
        const duration = this.dataset.duration;
        let javaDuration;
        const endTime = Date.now();
        let startTime;

        // Convert user-friendly duration format to Java Duration format
        switch (duration) {
            case '15m':
                javaDuration = 'PT15M';
                startTime = endTime - (15 * 60 * 1000);
                break;
            case '30m':
                javaDuration = 'PT30M';
                startTime = endTime - (30 * 60 * 1000);
                break;
            case '1h':
                javaDuration = 'PT1H';
                startTime = endTime - (60 * 60 * 1000);
                break;
            case '4h':
                javaDuration = 'PT4H';
                startTime = endTime - (4 * 60 * 60 * 1000);
                break;
            case '8h':
                javaDuration = 'PT8H';
                startTime = endTime - (8 * 60 * 60 * 1000);
                break;
            case '24h':
                javaDuration = 'P1D';
                startTime = endTime - (24 * 60 * 60 * 1000);
                break;
            case '7d':
                javaDuration = 'P7D';
                startTime = endTime - (7 * 24 * 60 * 60 * 1000);
                break;
        }

        // Update UI state to highlight the active button
        document.querySelectorAll('#defaultDurationButtons button').forEach(btn => {
            btn.dataset.active = btn === this ? 'true' : 'false';
        });

        // Store the raw UTC start/end times for API calls
        state.positivePctChangeTimeRange = { startTime: startTime, endTime: endTime, duration: javaDuration };
        console.log("Positive range set by duration button (raw UTC):", state.positivePctChangeTimeRange);
        // Update the UI with timezone-adjusted times
        updateAnalysisButtonText('positivePctChangeButton', adjustTimestampForDisplay(startTime), adjustTimestampForDisplay(endTime));
        document.getElementById('positivePctChangeButton').dataset.hasData = 'true';

        // Clear the negative range when a default duration is selected
        state.negativePctChangeTimeRange = { startTime: null, endTime: null, duration: null };
        const negButton = document.getElementById('negativePctChangeButton');
        updateAnalysisButtonText('negativePctChangeButton', null, null); // Reset text
        negButton.dataset.hasData = 'false'; // Mark as no data
        negButton.dataset.active = 'false'; // Ensure it's not marked as active for selection

        // Update and render markers after setting range via duration button
        updateRangeMarkers();
        renderAllChartMarkers();

        const posRangeSet = state.positivePctChangeTimeRange.startTime || state.positivePctChangeTimeRange.duration;

        // Update time displays with timezone-adjusted times
        document.getElementById('selectedStartTime').textContent = formatDateTime(adjustTimestampForDisplay(startTime));
        document.getElementById('selectedEndTime').textContent = formatDateTime(adjustTimestampForDisplay(endTime));
        document.getElementById('selectedDuration').textContent = formatDuration(startTime, endTime);
        document.getElementById('selectedTimeInfo').classList.remove('hidden');

        // Fetch price changes if the positive range is set
        if (posRangeSet) {
            fetchPriceChanges(
                state.positivePctChangeTimeRange.startTime, state.positivePctChangeTimeRange.endTime, state.positivePctChangeTimeRange.duration,
                null, null, null // Pass null for negative range as it's cleared
            );
        }
    });
});

// Event Listener - Auto Trend Button
document.getElementById('autoTrendButton').addEventListener('click', async () => {
    const autoButton = document.getElementById('autoTrendButton');
    const posButton = document.getElementById('positivePctChangeButton');
    const negButton = document.getElementById('negativePctChangeButton');

    // Optional: Add loading state to the button
    autoButton.disabled = true;
    autoButton.innerHTML = `<i class="fas fa-spinner fa-spin mr-2"></i><span>Loading...</span>`;

    try {
        const response = await fetch(`${window.location.origin}/api/crypto/dominance/trends?minChange=0.1&minHours=6&maxHours=24&hours=48`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const trends = await response.json();

        // Assign downtrend to positive range
        if (trends.latestDowntrend) {
            const startTime = new Date(trends.latestDowntrend.startTime).getTime();
            const endTime = new Date(trends.latestDowntrend.endTime).getTime();
            state.positivePctChangeTimeRange = { startTime, endTime, duration: null };
            updateAnalysisButtonText('positivePctChangeButton', adjustTimestampForDisplay(startTime), adjustTimestampForDisplay(endTime));
            posButton.dataset.hasData = 'true';
        }

        // Assign uptrend to negative range
        if (trends.latestUptrend) {
            const startTime = new Date(trends.latestUptrend.startTime).getTime();
            const endTime = new Date(trends.latestUptrend.endTime).getTime();
            state.negativePctChangeTimeRange = { startTime, endTime, duration: null };
            updateAnalysisButtonText('negativePctChangeButton', adjustTimestampForDisplay(startTime), adjustTimestampForDisplay(endTime));
            negButton.dataset.hasData = 'true';
        }

        // Update and render markers after auto-trend sets ranges
        updateRangeMarkers();
        renderAllChartMarkers();

        // Fetch price changes if the positive range is set
        const posRangeSet = posButton.dataset.hasData === 'true';
        const negRangeSet = negButton.dataset.hasData === 'true';

        if (posRangeSet) {
            // Determine negative range parameters to pass
            const negStartTimeParam = negRangeSet ? state.negativePctChangeTimeRange.startTime : null;
            const negEndTimeParam = negRangeSet ? state.negativePctChangeTimeRange.endTime : null;
            const negDurationParam = negRangeSet ? state.negativePctChangeTimeRange.duration : null;

            await fetchPriceChanges(
                state.positivePctChangeTimeRange.startTime, state.positivePctChangeTimeRange.endTime, state.positivePctChangeTimeRange.duration,
                negStartTimeParam,
                negEndTimeParam,
                negDurationParam
            );
        }
    } catch (error) {
        console.error('Error fetching or processing trends:', error);
        // Optional: Show an error message to the user
    } finally {
        // Restore button state
        autoButton.disabled = false;
        autoButton.innerHTML = `<i class="fas fa-robot mr-2"></i><span>Auto</span>`;
    }
});

// Linear Gauge Functions

/**
 * Fetch stats for all three metrics (weekly, monthly, yearly)
 */
async function fetchDominanceStats() {
    try {
        const response = await fetch(`${window.location.origin}/api/crypto/dominance/combined-stats`);
        if (!response.ok) {
            console.error('Failed to fetch combined stats:', response.statusText);
            return;
        }

        allDominanceStats = await response.json();
        console.log('Fetched combined stats:', allDominanceStats);

        // Update gauges with the new stats
        updateLinearGauges();
        // Update strategy advice as well
        updateStrategyAdvice();
    } catch (error) {
        console.error('Error fetching dominance stats:', error);
    }
}

/**
 * Update all three linear gauges with current data
 */
function updateLinearGauges() {
    const stats = allDominanceStats.find(s => s.timeframe === selectedDominanceTimeframe);
    if (!stats) {
        console.warn(`Stats for timeframe ${selectedDominanceTimeframe} not available yet`);
        return;
    }

    // Update Dominance Difference gauge
    if (currentDominanceDifference !== null && stats.dom_diff_min !== undefined) {
        const domDiffStats = {
            min: stats.dom_diff_min,
            p25: stats.dom_diff_p25,
            p37_5: stats.dom_diff_p37_5,
            median: stats.dom_diff_median,
            p62_5: stats.dom_diff_p62_5,
            p75: stats.dom_diff_p75,
            p99: stats.dom_diff_p99,
            max: stats.dom_diff_max
        };
        updateLinearGauge('dominance-difference', currentDominanceDifference, domDiffStats);
    }

    // Update BTC to Top 10 Alt gauge
    if (currentBtcToTop10Alt !== null && stats.btc_top10_min !== undefined) {
        const btcTop10Stats = {
            min: stats.btc_top10_min,
            p25: stats.btc_top10_p25,
            p37_5: stats.btc_top10_p37_5,
            median: stats.btc_top10_median,
            p62_5: stats.btc_top10_p62_5,
            p75: stats.btc_top10_p75,
            p99: stats.btc_top10_p99,
            max: stats.btc_top10_max
        };
        updateLinearGauge('btc-to-top10-alt', currentBtcToTop10Alt, btcTop10Stats);
    }

    // Update Top 10 to Small Alt gauge
    if (currentTop10ToSmallAlt !== null && stats.top10_small_min !== undefined) {
        const top10SmallStats = {
            min: stats.top10_small_min,
            p25: stats.top10_small_p25,
            p37_5: stats.top10_small_p37_5,
            median: stats.top10_small_median,
            p62_5: stats.top10_small_p62_5,
            p75: stats.top10_small_p75,
            p99: stats.top10_small_p99,
            max: stats.top10_small_max
        };
        updateLinearGauge('top10-to-small-alt', currentTop10ToSmallAlt, top10SmallStats);
    }
}

/**
 * Calculate percentage widths for each zone based on quartiles
 * @param {Object} stats - Stats object containing min, p25, median, p75, max
 * @returns {Object} Object with zone1, zone2, zone3, zone4 percentage widths
 */
function calculateZoneWidths(stats) {
    // Handle null or undefined stats
    if (!stats || stats.min === null || stats.max === null) {
        // Return equal widths when stats are invalid (100 / 7 = 14.28...)
        return { zone1: 14.28, zone2: 14.28, zone3: 14.28, zone4: 14.28, zone5: 14.28, zone6: 14.28, zone7: 14.32 };
    }

    const { min, p25, median, p75, max } = stats;
    // Use provided percentiles or fallback to linear interpolation
    const p37_5 = stats.p37_5 !== undefined ? stats.p37_5 : (p25 + median) / 2;
    const p62_5 = stats.p62_5 !== undefined ? stats.p62_5 : (median + p75) / 2;
    const p99 = stats.p99 !== undefined ? stats.p99 : p75 + (max - p75) * 0.96; // Fallback if missing

    // Handle edge case: zero range (min === max)
    const range = max - min;
    if (range === 0) {
        // Return equal widths when range is zero
        return { zone1: 14.28, zone2: 14.28, zone3: 14.28, zone4: 14.28, zone5: 14.28, zone6: 14.28, zone7: 14.32 };
    }

    // Handle invalid quartiles (not in ascending order)
    if (p25 < min || p37_5 < p25 || median < p37_5 || p62_5 < median || p75 < p62_5 || p99 < p75 || max < p99) {
        console.warn('Invalid quartiles detected, using equal zone widths');
        return { zone1: 14.28, zone2: 14.28, zone3: 14.28, zone4: 14.28, zone5: 14.28, zone6: 14.28, zone7: 14.32 };
    }

    // Calculate percentage widths for each zone
    const zone1 = ((p25 - min) / range) * 100;
    const zone2 = ((p37_5 - p25) / range) * 100;
    const zone3 = ((median - p37_5) / range) * 100;
    const zone4 = ((p62_5 - median) / range) * 100;
    const zone5 = ((p75 - p62_5) / range) * 100;
    const zone6 = ((p99 - p75) / range) * 100;
    const zone7 = ((max - p99) / range) * 100;

    return { zone1, zone2, zone3, zone4, zone5, zone6, zone7 };
}

/**
 * Calculate indicator position percentage from current value
 * @param {number} value - Current value
 * @param {number} min - Minimum value
 * @param {number} max - Maximum value
 * @returns {number} Position percentage (0-100), clamped to range
 */
function calculatePosition(value, min, max) {
    // Handle null/undefined/NaN values
    if (value === null || value === undefined || isNaN(value)) {
        return null;
    }

    // Handle null/undefined min/max
    if (min === null || min === undefined || max === null || max === undefined) {
        return null;
    }

    const range = max - min;

    // Handle zero range
    if (range === 0) {
        return 50; // Position at middle when range is zero
    }

    // Calculate position
    const position = ((value - min) / range) * 100;

    // Clamp to 0-100% range
    return Math.max(0, Math.min(100, position));
}

/**
 * Format numeric values to 2 decimal places
 * @param {number} value - Numeric value to format
 * @returns {string} Formatted value or 'N/A' for invalid values
 */
function formatGaugeValue(value) {
    // Handle null/undefined/NaN values
    if (value === null || value === undefined || isNaN(value)) {
        return 'N/A';
    }

    return value.toFixed(2);
}

/**
 * Update a linear gauge with new data
 * @param {string} gaugeId - ID prefix for the gauge elements (e.g., 'dominance-difference')
 * @param {number} currentValue - Current metric value
 * @param {Object} stats - Stats object containing min, p25, median, p75, max
 */
function updateLinearGauge(gaugeId, currentValue, stats) {
    // Get gauge elements
    const gaugeElement = document.querySelector(`[data-gauge-id="${gaugeId}"]`);
    if (!gaugeElement) {
        console.warn(`Gauge element not found for ID: ${gaugeId}`);
        return;
    }

    const zones = gaugeElement.querySelectorAll('.gauge-zone');
    const indicator = gaugeElement.querySelector('.gauge-indicator');
    const valueDisplay = gaugeElement.querySelector('.value-number');
    const labels = {
        min: gaugeElement.querySelector('.label-min'),
        p25: gaugeElement.querySelector('.label-p25'),
        p37_5: gaugeElement.querySelector('.label-p37-5'),
        median: gaugeElement.querySelector('.label-median'),
        p62_5: gaugeElement.querySelector('.label-p62-5'),
        p75: gaugeElement.querySelector('.label-p75'),
        p99: gaugeElement.querySelector('.label-p99'),
        max: gaugeElement.querySelector('.label-max')
    };

    // Handle missing stats - show loading state
    if (!stats || stats.min === null || stats.max === null) {
        if (valueDisplay) valueDisplay.textContent = 'Loading...';
        if (indicator) indicator.style.display = 'none';
        return;
    }

    // Calculate and update zone widths
    const zoneWidths = calculateZoneWidths(stats);
    if (zones.length >= 7) {
        zones[0].style.width = `${zoneWidths.zone1}%`;
        zones[1].style.width = `${zoneWidths.zone2}%`;
        zones[2].style.width = `${zoneWidths.zone3}%`;
        zones[3].style.width = `${zoneWidths.zone4}%`;
        zones[4].style.width = `${zoneWidths.zone5}%`;
        zones[5].style.width = `${zoneWidths.zone6}%`;
        zones[6].style.width = `${zoneWidths.zone7}%`;
    } else if (zones.length >= 6) {
        // Fallback for 6 zones (if HTML not updated)
        const totalZone1 = zoneWidths.zone1; // min to p25
        const totalZone2 = zoneWidths.zone2; // p25 to p37.5
        const totalZone3 = zoneWidths.zone3; // p37.5 to median
        const totalZone4 = zoneWidths.zone4; // median to p62.5
        const totalZone5 = zoneWidths.zone5; // p62.5 to p75
        const totalZone6 = zoneWidths.zone6 + zoneWidths.zone7; // p75 to max (merging p99 zone)
        
        zones[0].style.width = `${totalZone1}%`;
        zones[1].style.width = `${totalZone2}%`;
        zones[2].style.width = `${totalZone3}%`;
        zones[3].style.width = `${totalZone4}%`;
        zones[4].style.width = `${totalZone5}%`;
        zones[5].style.width = `${totalZone6}%`;
    } else if (zones.length >= 4) {
        // Fallback for 4 zones (old HTML)
        const totalZone1 = zoneWidths.zone1 + zoneWidths.zone2; // min to p37.5 (approx p25)
        const totalZone2 = zoneWidths.zone3; // p37.5 to median
        const totalZone3 = zoneWidths.zone4; // median to p62.5
        const totalZone4 = zoneWidths.zone5 + zoneWidths.zone6 + zoneWidths.zone7; // p62.5 to max (approx p75)
        
        zones[0].style.width = `${totalZone1}%`;
        zones[1].style.width = `${totalZone2}%`;
        zones[2].style.width = `${totalZone3}%`;
        zones[3].style.width = `${totalZone4}%`;
    }

    // Calculate label positions based on zone widths
    const labelPositions = {
        min: 0,
        p25: zoneWidths.zone1,
        p37_5: zoneWidths.zone1 + zoneWidths.zone2,
        median: zoneWidths.zone1 + zoneWidths.zone2 + zoneWidths.zone3,
        p62_5: zoneWidths.zone1 + zoneWidths.zone2 + zoneWidths.zone3 + zoneWidths.zone4,
        p75: zoneWidths.zone1 + zoneWidths.zone2 + zoneWidths.zone3 + zoneWidths.zone4 + zoneWidths.zone5,
        p99: zoneWidths.zone1 + zoneWidths.zone2 + zoneWidths.zone3 + zoneWidths.zone4 + zoneWidths.zone5 + zoneWidths.zone6,
        max: 100
    };

    // Update label values and positions
    if (labels.min) {
        labels.min.textContent = formatGaugeValue(stats.min);
        labels.min.style.left = `${labelPositions.min}%`;
    }
    if (labels.p25) {
        labels.p25.textContent = formatGaugeValue(stats.p25);
        labels.p25.style.left = `${labelPositions.p25}%`;
    }
    if (labels.p37_5) {
        labels.p37_5.textContent = formatGaugeValue(stats.p37_5);
        labels.p37_5.style.left = `${labelPositions.p37_5}%`;
    }
    if (labels.median) {
        labels.median.textContent = formatGaugeValue(stats.median);
        labels.median.style.left = `${labelPositions.median}%`;
    }
    if (labels.p62_5) {
        labels.p62_5.textContent = formatGaugeValue(stats.p62_5);
        labels.p62_5.style.left = `${labelPositions.p62_5}%`;
    }
    if (labels.p75) {
        labels.p75.textContent = formatGaugeValue(stats.p75);
        labels.p75.style.left = `${labelPositions.p75}%`;
    }
    if (labels.p99) {
        labels.p99.textContent = formatGaugeValue(stats.p99);
        labels.p99.style.left = `${labelPositions.p99}%`;
    }
    if (labels.max) {
        labels.max.textContent = formatGaugeValue(stats.max);
        labels.max.style.left = `${labelPositions.max}%`;
    }

    // Update current value display
    if (valueDisplay) {
        valueDisplay.textContent = formatGaugeValue(currentValue);
    }

    // Calculate and update indicator position
    const position = calculatePosition(currentValue, stats.min, stats.max);
    if (indicator) {
        if (position === null) {
            // Hide indicator for invalid values
            indicator.style.display = 'none';
        } else {
            indicator.style.display = 'block';
            indicator.style.left = `${position}%`;
        }
    }
}

// SSE Listener - Real-time Dominance Updates
function setupSSE() {
    const eventSource = new EventSource(`${window.location.origin}/sse/dominance-updates`);

    eventSource.onmessage = function (event) {
        try {
            if (event.data === "DominanceMetricUpdatedEvent") {
                // Force refresh when SSE event is received
                fetchData(true);
            }
        } catch (e) {
            console.error("Error parsing SSE message:", e);
            console.error("Raw message data:", event.data);
        }
    };

    eventSource.onerror = function (error) {
        console.debug("SSE connection error:", error);
        eventSource.close(); // Close the current connection
        console.log("Retrying SSE connection in 3 seconds...");
        // Fetch latest data and retry SSE connection
        Promise.all([
            fetchData(true), // Force refresh when reconnecting
            fetchDominanceDifferenceStats(),
            fetchGiniStats()
        ]).catch(error => {
            console.error('Error during data refetch:', error);
        });
        setTimeout(setupSSE, 3000); // Retry after 3 seconds
    };
}


// DOMContentLoaded Event Listener - Initial Data Load on Page Load
document.addEventListener('DOMContentLoaded', function () {

    // --- Timezone Selector Logic ---
    const timezoneSelector = document.getElementById('timezoneSelector');
    if (timezoneSelector) {
        const allTimezones = moment.tz.names();
        const guessedTimezone = moment.tz.guess(); // Can be null
        const defaultTimezone = guessedTimezone || 'Etc/UTC';
        state.selectedTimezone = defaultTimezone; // Set initial global timezone

        const popularTimezones = [
            'America/New_York', 'America/Los_Angeles', 'Europe/London',
            'Europe/Berlin', 'Asia/Tokyo', 'Asia/Shanghai', 'Australia/Sydney'
        ];

        // Build the prioritized list
        let sortedTimezones = [];
        const addedTimezones = new Set();

        // 1. Add UTC
        if (allTimezones.includes('Etc/UTC')) {
            sortedTimezones.push('Etc/UTC');
            addedTimezones.add('Etc/UTC');
        }

        // 2. Add guessed timezone (if valid and not already added)
        if (guessedTimezone && allTimezones.includes(guessedTimezone) && !addedTimezones.has(guessedTimezone)) {
            sortedTimezones.push(guessedTimezone);
            addedTimezones.add(guessedTimezone);
        }

        // 3. Add popular timezones (if valid and not already added)
        popularTimezones.forEach(tz => {
            if (allTimezones.includes(tz) && !addedTimezones.has(tz)) {
                sortedTimezones.push(tz);
                addedTimezones.add(tz);
            }
        });

        // 4. Add a separator (optional, but nice)
        sortedTimezones.push('---'); // Use a value that won't be a real timezone

        // 5. Add the rest alphabetically
        allTimezones.sort().forEach(tz => {
            if (!addedTimezones.has(tz)) {
                sortedTimezones.push(tz);
            }
        });

        // Populate the selector
        sortedTimezones.forEach(tz => {
            const option = document.createElement('option');
            if (tz === '---') {
                option.disabled = true;
                option.textContent = '────────────────────';
            } else {
                option.value = tz;
                option.textContent = tz.replace(/_/g, ' '); // Make names more readable
                if (tz === defaultTimezone) {
                    option.selected = true;
                }
            }
            timezoneSelector.appendChild(option);
        });

        timezoneSelector.addEventListener('change', async function () {
            state.selectedTimezone = this.value;

            // Re-process the main chart series data with the new timezone.
            await processDominanceData(null); // This re-renders the main series and extreme markers

            // Explicitly update the range markers with the new timezone
            updateRangeMarkers();
            // Re-render all markers to apply the changes
            renderAllChartMarkers();
            // Update any displayed dates/times in the UI, ensuring they are adjusted for display
            if (!document.getElementById('selectedTimeInfo').classList.contains('hidden')) {
                if (state.positivePctChangeTimeRange.startTime) {
                    document.getElementById('selectedStartTime').textContent = formatDateTime(adjustTimestampForDisplay(state.positivePctChangeTimeRange.startTime));
                    document.getElementById('selectedEndTime').textContent = formatDateTime(adjustTimestampForDisplay(state.positivePctChangeTimeRange.endTime));
                }
            }
            // Update analysis button text with adjusted times
            if (state.positivePctChangeTimeRange.startTime && state.positivePctChangeTimeRange.endTime) {
                updateAnalysisButtonText('positivePctChangeButton', adjustTimestampForDisplay(state.positivePctChangeTimeRange.startTime), adjustTimestampForDisplay(state.positivePctChangeTimeRange.endTime));
            }
            if (state.negativePctChangeTimeRange.startTime && state.negativePctChangeTimeRange.endTime) {
                updateAnalysisButtonText('negativePctChangeButton', adjustTimestampForDisplay(state.negativePctChangeTimeRange.startTime), adjustTimestampForDisplay(state.negativePctChangeTimeRange.endTime));
            }
        });
    } else {
        console.warn("Timezone selector element not found.");
        state.selectedTimezone = 'Etc/UTC'; // Default if selector missing
    }
    // --- End Timezone Selector Logic ---

    // Set the active state for the default time range button
    const defaultButton = document.querySelector(`[data-time="${DEFAULT_TIME_RANGE}"]`);
    if (defaultButton) {
        setActiveButton(defaultButton);
    }

    // Process pre-fetched data once DOM is ready
    Promise.all([
        fetchData(), // fetchData will use the updated activeTimeRange
        fetchDominanceDifferenceStats(),
        fetchGiniStats(),
        fetchDominanceStats(), // Fetch combined stats for linear gauges
        fetchLatestDominanceData()
    ]).catch(error => {
        console.error('Error during data processing:', error);
    });

    setupSSE(); // Initial setup of SSE connection

    // Add event listener for toggle button if it exists in HTML
    const toggleButton = document.getElementById('toggleExtremesButton');
    if (toggleButton) {
        toggleButton.addEventListener('click', toggleExtremeMarkers);
    }

    // --- Dominance Meter Hover Logic ---
    const dominanceMeter = document.getElementById('dominanceMeter');
    const dominanceMeterTooltip = document.getElementById('dominanceMeterTooltip');

    if (dominanceMeter && dominanceMeterTooltip) {
        dominanceMeter.addEventListener('mouseenter', () => {
            if (minDominanceDifference !== null && maxDominanceDifference !== null) {
                dominanceMeterTooltip.style.display = 'block';
            }
        });

        dominanceMeter.addEventListener('mouseleave', () => {
            dominanceMeterTooltip.style.display = 'none';
        });

        dominanceMeter.addEventListener('mousemove', (e) => {
            if (minDominanceDifference === null || maxDominanceDifference === null) return;

            const rect = dominanceMeter.getBoundingClientRect();
            const x = e.clientX - rect.left; // X position relative to the meter element
            const width = rect.width;

            // Calculate position percentage (0 to 1)
            const positionRatio = Math.max(0, Math.min(1, x / width));

            // Calculate the corresponding Dominance Difference value
            const range = maxDominanceDifference - minDominanceDifference;
            const hoveredValue = minDominanceDifference + (range * positionRatio);

            // Update tooltip content and position
            dominanceMeterTooltip.textContent = hoveredValue.toFixed(2);

            // Position the tooltip center above the mouse cursor
            // The tooltip's transform: translateX(-50%) centers it horizontally.
            dominanceMeterTooltip.style.left = `${x}px`;
        });
    }
    // --- End Dominance Meter Hover Logic ---

    // --- Gini Meter Hover Logic ---
    const giniMeter = document.getElementById('giniMeter');
    const giniMeterTooltip = document.getElementById('giniMeterTooltip');

    if (giniMeter && giniMeterTooltip) {
        giniMeter.addEventListener('mouseenter', () => {
            if (giniStats && giniStats.minGiniIndex !== null && giniStats.maxGiniIndex !== null) {
                giniMeterTooltip.style.display = 'block';
            }
        });

        giniMeter.addEventListener('mouseleave', () => {
            giniMeterTooltip.style.display = 'none';
        });

        giniMeter.addEventListener('mousemove', (e) => {
            if (!giniStats || giniStats.minGiniIndex === null || giniStats.maxGiniIndex === null) return;

            const rect = giniMeter.getBoundingClientRect();
            const x = e.clientX - rect.left; // X position relative to the meter element
            const width = rect.width;

            // Calculate position percentage (0 to 1)
            const positionRatio = Math.max(0, Math.min(1, x / width));

            // Calculate the corresponding Gini Index value
            const range = giniStats.maxGiniIndex - giniStats.minGiniIndex;
            const hoveredValue = giniStats.minGiniIndex + (range * positionRatio);

            // Update tooltip content and position
            giniMeterTooltip.textContent = hoveredValue.toFixed(4);

            // Position the tooltip center above the mouse cursor
            giniMeterTooltip.style.left = `${x}px`;
        });
    }
    // --- End Gini Meter Hover Logic ---

    document.getElementById('autoTrendButton').click();
});

// Add a function to toggle between USD and BTC price changes
function togglePriceChangeUnit() {
    priceChangeInUSD = !priceChangeInUSD;

    // Update button states
    document.querySelectorAll('[data-unit]').forEach(btn => {
        btn.dataset.active = btn.dataset.unit === (priceChangeInUSD ? 'usd' : 'btc') ? 'true' : 'false';
    });

    // Refetch only if both time ranges are set
    const posRangeSet = state.positivePctChangeTimeRange.startTime || state.positivePctChangeTimeRange.duration;
    const negRangeSet = state.negativePctChangeTimeRange.startTime || state.negativePctChangeTimeRange.duration;

    if (posRangeSet && negRangeSet) {
        // Refetch using the stored time ranges
        fetchPriceChanges(
            state.positivePctChangeTimeRange.startTime, state.positivePctChangeTimeRange.endTime, state.positivePctChangeTimeRange.duration,
            state.negativePctChangeTimeRange.startTime, state.negativePctChangeTimeRange.endTime, state.negativePctChangeTimeRange.duration
        );
    }
}

// Add event listeners for the unit toggle buttons
document.addEventListener('DOMContentLoaded', function () {

    // Add event listeners for USD/BTC toggle buttons
    document.querySelectorAll('[data-unit]').forEach(button => {
        button.addEventListener('click', function () {
            const newUnit = this.dataset.unit;
            priceChangeInUSD = newUnit === 'usd';

            // Update button states
            document.querySelectorAll('[data-unit]').forEach(btn => {
                btn.dataset.active = btn.dataset.unit === newUnit ? 'true' : 'false';
            });

            // Refetch data if the positive time range is set
            const posRangeSet = state.positivePctChangeTimeRange.startTime || state.positivePctChangeTimeRange.duration;
            const negRangeSet = state.negativePctChangeTimeRange.startTime || state.negativePctChangeTimeRange.duration; // Check if negative is set too

            if (posRangeSet) {
                // Determine negative range parameters to pass
                const negStartTimeParam = negRangeSet ? state.negativePctChangeTimeRange.startTime : null;
                const negEndTimeParam = negRangeSet ? state.negativePctChangeTimeRange.endTime : null;
                const negDurationParam = negRangeSet ? state.negativePctChangeTimeRange.duration : null;

                // Refetch using the stored time ranges
                fetchPriceChanges(
                    state.positivePctChangeTimeRange.startTime, state.positivePctChangeTimeRange.endTime, state.positivePctChangeTimeRange.duration,
                    negStartTimeParam,
                    negEndTimeParam,
                    negDurationParam
                );
            }
        });
    });
});

// Initiate data fetching before DOM is ready
// These promises will be used inside DOMContentLoaded
// Calculate time range for the default activeTimeRange ('3M')
const endTime = new Date().getTime();
const startTime = endTime - TIME_RANGES[DEFAULT_TIME_RANGE]; // Use the default DEFAULT_TIME_RANGE from activeTimeRange
const startTimeISO = new Date(startTime).toISOString();
const endTimeISO = new Date(endTime).toISOString();

// Pre-fetch data with the default time range to avoid duplicate API calls
const dominanceDataPromise = fetch(`${window.location.origin}/api/crypto/dominance/btc?startTime=${startTimeISO}&endTime=${endTimeISO}`)
    .then(response => response.json())
    .catch(error => {
        console.error('Error pre-fetching dominance data:', error);
        return null;
    });

const dominanceDifferenceStatsPromise = fetch(`${window.location.origin}/api/crypto/dominance/difference/summary`)
    .then(response => response.json())
    .catch(error => {
        console.error('Error pre-fetching dominance difference stats:', error);
        return null;
    });

const giniStatsPromise = fetch(`${window.location.origin}/api/crypto/dominance/gini/summary`)
    .then(response => response.json())
    .catch(error => {
        console.error('Error pre-fetching Gini stats:', error);
        return null;
    });

// Pre-fetch extreme timestamps - simplified for dominanceDifference only
const extremeTimestampsPromise = fetch(`${window.location.origin}/api/crypto/dominance/extreme-timestamps?extremeType=dominanceDifference`)
    .then(response => response.json())
    .then(data => {
        state.extremeTimestamps = data;
        return data;
    })
    .catch(error => {
        console.error('Error pre-fetching extreme timestamps:', error);
        return [];
    });

// Function to fetch extreme timestamps - simplified for dominanceDifference only
async function fetchExtremeTimestamps() {
    try {
        const response = await fetch(`${window.location.origin}/api/crypto/dominance/extreme-timestamps?extremeType=dominanceDifference`);
        if (!response.ok) {
            console.error('Failed to fetch extreme timestamps:', response.statusText);
            return;
        }

        // Store all fetched extreme timestamps without filtering by chart range yet
        state.extremeTimestamps = await response.json();
        console.log(`Fetched ${state.extremeTimestamps.length} total extreme timestamps.`);

    } catch (error) {
        console.error('Error fetching extreme timestamps:', error);
    }
}

// Pre-fetch latest dominance data for fast initial load
const latestDominanceDataPromise = fetch(`${window.location.origin}/api/crypto/dominance/btc/latest`)
    .then(response => response.json())
    .catch(error => {
        console.error('Error pre-fetching latest dominance data:', error);
        return null;
    });

const latestZScoreDataPromise = fetch(`${window.location.origin}/api/crypto/dominance/zscores/latest`)
    .then(response => response.json())
    .catch(error => {
        console.error('Error pre-fetching latest z-score data:', error);
        return null;
    });

/**
 * Fetch and process the latest dominance data to update UI quickly
 */
async function fetchLatestDominanceData() {
    try {
        const [latestData, zScoreData] = await Promise.all([
            latestDominanceDataPromise,
            latestZScoreDataPromise
        ]);

        if (latestData) {
            console.log("Fetched latest dominance data for initial load:", latestData);

            // Update global dominance values
            currentDominanceDifference = latestData.dominanceDifference;
            currentGiniIndex = latestData.giniAltExclTop10Stable;
            currentBtcToTop10Alt = latestData.btcToTop10AltDominanceLogRatio;
            currentTop10ToSmallAlt = latestData.top10ToSmallAltLogRatio;

            // Update UI components
            updateDominanceMeter();
            updateGiniMeter();
            updateLinearGauges();
            updatePageTitle(currentDominanceDifference);
        }

        if (zScoreData) {
            console.log("Fetched latest z-score data for initial load:", zScoreData);
            updateZScoreSignals(zScoreData.zscore4d, zScoreData.zscore21d);
        }
    } catch (error) {
        console.error('Error processing latest dominance/z-score data:', error);
    }
}

/**
 * Update z-score display with trading signal highlighting
 * LONG when 21d z-score <= -2.2
 * SHORT when 4d z-score >= 3.4
 * TAKE PROFIT when z-score is near 0 (between -0.5 and 0.5)
 */
function updateZScoreSignals(zscore4d, zscore21d) {
    const LONG_THRESHOLD = -2.2;
    const SHORT_THRESHOLD = 3.4;
    const TP_LOW = -0.5;
    const TP_HIGH = 0.5;

    // --- 4D Z-Score ---
    const z4dEl = document.getElementById('zscore-4d');
    const z4dCard = document.getElementById('zscore-4d-card');
    const z4dSignal = document.getElementById('zscore-4d-signal');
    const z4dIcon = document.getElementById('zscore-4d-icon');

    if (z4dEl && zscore4d !== null && zscore4d !== undefined) {
        z4dEl.textContent = zscore4d.toFixed(2);

        // Reset classes
        z4dCard.classList.remove('signal-long', 'signal-short', 'signal-tp');
        z4dSignal.style.display = 'none';
        z4dSignal.classList.remove('badge-long', 'badge-short', 'badge-tp', 'badge-active');
        z4dIcon.style.display = 'none';

        if (zscore4d >= SHORT_THRESHOLD) {
            // SHORT signal
            z4dEl.className = 'text-2xl font-bold text-red-400';
            z4dCard.classList.add('signal-short');
            z4dSignal.innerHTML = '<i class="fas fa-arrow-trend-down"></i> SHORT';
            z4dSignal.classList.add('badge-short', 'badge-active');
            z4dSignal.style.display = 'inline-flex';
            z4dIcon.className = 'fas fa-triangle-exclamation text-sm text-red-400';
            z4dIcon.style.display = 'inline';
        } else if (zscore4d >= TP_LOW && zscore4d <= TP_HIGH) {
            // TAKE PROFIT zone
            z4dEl.className = 'text-2xl font-bold text-amber-400';
            z4dCard.classList.add('signal-tp');
            z4dSignal.innerHTML = '<i class="fas fa-hand-holding-dollar"></i> TAKE PROFIT';
            z4dSignal.classList.add('badge-tp');
            z4dSignal.style.display = 'inline-flex';
        } else {
            // Neutral
            z4dEl.className = `text-2xl font-bold ${zscore4d >= 0 ? 'text-green-400' : 'text-red-400'}`;
        }
    }

    // --- 21D Z-Score ---
    const z21dEl = document.getElementById('zscore-21d');
    const z21dCard = document.getElementById('zscore-21d-card');
    const z21dSignal = document.getElementById('zscore-21d-signal');
    const z21dIcon = document.getElementById('zscore-21d-icon');

    if (z21dEl && zscore21d !== null && zscore21d !== undefined) {
        z21dEl.textContent = zscore21d.toFixed(2);

        // Reset classes
        z21dCard.classList.remove('signal-long', 'signal-short', 'signal-tp');
        z21dSignal.style.display = 'none';
        z21dSignal.classList.remove('badge-long', 'badge-short', 'badge-tp', 'badge-active');
        z21dIcon.style.display = 'none';

        if (zscore21d <= LONG_THRESHOLD) {
            // LONG signal
            z21dEl.className = 'text-2xl font-bold text-emerald-400';
            z21dCard.classList.add('signal-long');
            z21dSignal.innerHTML = '<i class="fas fa-arrow-trend-up"></i> LONG';
            z21dSignal.classList.add('badge-long', 'badge-active');
            z21dSignal.style.display = 'inline-flex';
            z21dIcon.className = 'fas fa-rocket text-sm text-emerald-400';
            z21dIcon.style.display = 'inline';
        } else if (zscore21d >= TP_LOW && zscore21d <= TP_HIGH) {
            // TAKE PROFIT zone
            z21dEl.className = 'text-2xl font-bold text-amber-400';
            z21dCard.classList.add('signal-tp');
            z21dSignal.innerHTML = '<i class="fas fa-hand-holding-dollar"></i> TAKE PROFIT';
            z21dSignal.classList.add('badge-tp');
            z21dSignal.style.display = 'inline-flex';
        } else {
            // Neutral
            z21dEl.className = `text-2xl font-bold ${zscore21d >= 0 ? 'text-green-400' : 'text-red-400'}`;
        }
    }
}